// Ultimate Card Game (UCG) - Base Module Tower Rendering
//
// Mixed onto Renderer.prototype (see the Object.assign() call at the bottom
// of renderer.js), like the building-attachment and weather mixins - every
// method here reads instance state (this.quality) or calls a sibling method.
//
// PERFORMANCE (docs/PERFORMANCE_PARADIGMS.md #7 "two-stage layer
// partitioning" and #8 "bitmap rasterisation"):
//
// A module is a heavy industrial steel-frame cube - corner columns, edge
// beams, gusset plates, foot pads, plus interior machinery (centrifuge drum,
// catwalk, stairs, railing, and accelerator tube). Drawn procedurally that is
// ~120 draw calls per module, and a full tower on both teams would add ~700+
// draw calls per frame on top of the two fortresses that already cost several
// hundred. So the entire STATIC geometry of a module is baked once per
// (moduleId, team) into an offscreen canvas - exactly what
// buildSpriteBitmap() does for units - and costs a single drawImage() per
// module per frame afterwards. Only genuinely live overlays (the spinning
// impeller, traveling plasma energy waves, load indicator lamps, descent cable,
// and apex radar/strobe) are drawn per frame, and each of those is
// quality-gated (INVARIANTS #4, zero heap allocations).
//
// DETERMINISM (INVARIANTS #1, PERFORMANCE_PARADIGMS #11): nothing in this
// file writes simulation state. The descent animation is driven purely by
// ModuleSystem.deployProgress(), i.e. by the module's own tick countdown, so
// both peers show the same frame of it without any extra synchronisation.

import {
  MODULE_W, MODULE_H, MODULE_TOP_D, MODULE_FRONT_H, MODULE_SKEW,
  MODULE_MAX, MODULE_DEFS, CONGRESS_BUFFS
} from '../modules.js';

// Weathered industrial steel & corrosion palette matching reference art.
// Frame steel is deliberately shared across teams to read as a cohesive
// heavy construction system; team identity is provided by accent lights & glow.
const STEEL_DARK = '#1a1d24';      // Deep oxidized iron
const STEEL_MID = '#464850';       // Industrial steel midtone
const STEEL_LIGHT = '#686b75';     // Aged steel surface
const STEEL_SPEC = '#cbd2dc';      // Matte metallic wear
const RUST_DARK = '#5c2410';       // Deep crust rust
const RUST_MID = '#8a3818';        // Warm corrosion rust
const RUST_LIGHT = '#b85423';      // Surface rust highlight
const RUST = RUST_MID;
const FOOT_PLATE = '#9aa0a6';
const FOOT_PLATE_DARK = '#5f666c';

// Fixed coil offsets along the accelerator tube (3 indicator coils for 0-3 loaded marines)
const COIL_OFFSETS = [11, 21, 31];

// Congress Module Proclamation Speech Bubble scream balloon path trace helper (zero-allocation)
function _traceCongressScreamPath(c, bx, by, bubbleW, bubbleH, tipX, tipY, isWest) {
  c.beginPath();
  c.moveTo(bx + 4, by + 4);
  c.lineTo(bx + bubbleW * 0.18, by - 8);
  c.lineTo(bx + bubbleW * 0.30, by + 3);
  c.lineTo(bx + bubbleW * 0.50, by - 11);
  c.lineTo(bx + bubbleW * 0.70, by + 3);
  c.lineTo(bx + bubbleW * 0.85, by - 8);
  c.lineTo(bx + bubbleW * 0.94, by + 3);
  c.lineTo(bx + bubbleW + 10, by - 6);

  c.lineTo(bx + bubbleW - 2, by + bubbleH * 0.25);
  c.lineTo(bx + bubbleW + 11, by + bubbleH * 0.48);
  c.lineTo(bx + bubbleW - 2, by + bubbleH * 0.72);
  c.lineTo(bx + bubbleW + 9, by + bubbleH + 8);

  if (isWest) {
    c.lineTo(bx + bubbleW * 0.80, by + bubbleH - 2);
    c.lineTo(bx + bubbleW * 0.65, by + bubbleH + 9);
    c.lineTo(bx + bubbleW * 0.50, by + bubbleH - 2);
    c.lineTo(bx + bubbleW * 0.35, by + bubbleH + 8);
    c.lineTo(bx + 38, by + bubbleH);
    c.lineTo(bx + 26, by + bubbleH + 12);
    c.lineTo(bx + 32, by + bubbleH + 16);
    c.lineTo(tipX, tipY);
    c.lineTo(bx + 14, by + bubbleH + 10);
    c.lineTo(bx + 16, by + bubbleH);
    c.lineTo(bx - 8, by + bubbleH + 7);
  } else {
    c.lineTo(bx + bubbleW - 16, by + bubbleH);
    c.lineTo(bx + bubbleW - 14, by + bubbleH + 10);
    c.lineTo(tipX, tipY);
    c.lineTo(bx + bubbleW - 32, by + bubbleH + 16);
    c.lineTo(bx + bubbleW - 26, by + bubbleH + 12);
    c.lineTo(bx + bubbleW - 38, by + bubbleH);
    c.lineTo(bx + bubbleW * 0.50, by + bubbleH + 9);
    c.lineTo(bx + bubbleW * 0.35, by + bubbleH - 2);
    c.lineTo(bx + bubbleW * 0.20, by + bubbleH + 8);
    c.lineTo(bx - 8, by + bubbleH + 7);
  }

  c.lineTo(bx - 2, by + bubbleH * 0.72);
  c.lineTo(bx - 11, by + bubbleH * 0.48);
  c.lineTo(bx - 2, by + bubbleH * 0.25);
  c.lineTo(bx - 9, by - 6);
  c.closePath();
}

// Pre-allocated scratch array for the Centrifuge blade sort (Invariante #1) -
// refilled and sorted in-place every frame instead of `.map().sort()`
// allocating two throwaway arrays + 4 objects per centrifuge module.
const _bladesScratch = [
  { angle: 0, sinA: 0, cosA: 0 },
  { angle: 0, sinA: 0, cosA: 0 },
  { angle: 0, sinA: 0, cosA: 0 },
  { angle: 0, sinA: 0, cosA: 0 },
];

// 17-step alpha look-up tables (0/16 .. 16/16) so per-frame fillStyle strings
// come from array indexing instead of a `.toFixed()` string allocation per
// call (PERFORMANCE_PARADIGMS #3).
function buildAlphaLUT(r, g, b) {
  const lut = new Array(17);
  for (let i = 0; i <= 16; i++) lut[i] = `rgba(${r}, ${g}, ${b}, ${(i / 16).toFixed(3)})`;
  return lut;
}
const ALPHA_LUT_WHITE = buildAlphaLUT(255, 255, 255);
const ALPHA_LUT_RED_BEACON = buildAlphaLUT(255, 34, 68);
const ALPHA_LUT_DUST_SLATE = buildAlphaLUT(203, 213, 225);
const ALPHA_LUT_DUST_ORANGE = buildAlphaLUT(253, 186, 116);

// Baked module and cap bitmaps, keyed "<moduleId>|<team>". Never invalidated
// during a match - the geometry depends on nothing that changes at runtime.
const moduleBitmapCache = new Map();

export function clearModuleBitmapCache() {
  for (const entry of moduleBitmapCache.values()) {
    if (entry?.canvas) {
      entry.canvas.width = 0;
      entry.canvas.height = 0;
    }
  }
  moduleBitmapCache.clear();
}

// How far above its resting place a module starts its cable descent.
const DESCENT_DROP = 170;

// Cap module (Industrial Apex Roof Superstructure) dimensions.
const CAP_W = 124;
const CAP_H = 76;

// ── Baking Helpers ─────────────────────────────────────────────────────

// Double-T profile steel girder (I-Beam) with recessed web, flange highlights,
// and weld-seam rust patina.
function drawIBeam(ctx, x, y, w, h, isVertical, isBright) {
  ctx.fillStyle = '#080c14';
  ctx.fillRect(x - 0.6, y - 0.6, w + 1.2, h + 1.2);
  ctx.fillStyle = isBright ? STEEL_LIGHT : STEEL_MID;
  ctx.fillRect(x, y, w, h);

  if (isVertical) {
    const flW = 1.1;
    const webX = x + flW;
    const webW = Math.max(1.2, w - 2 * flW);

    // Recessed web
    ctx.fillStyle = isBright ? '#272a33' : '#14171e';
    ctx.fillRect(webX, y, webW, h);

    // Outer flange highlight
    ctx.fillStyle = STEEL_SPEC;
    ctx.fillRect(x, y, flW, h);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 0.3, y, 0.5, h);

    // Inner flange edge
    ctx.fillStyle = isBright ? '#52555e' : '#23262e';
    ctx.fillRect(x + w - flW, y, flW, h);

    // Weld seam rust patina
    ctx.fillStyle = RUST_DARK;
    ctx.fillRect(webX, y + h * 0.15, webW, h * 0.7);
    ctx.fillStyle = RUST_MID;
    ctx.fillRect(webX, y + h * 0.25, Math.min(webW, 1.2), h * 0.45);
    ctx.fillRect(x + 0.5, y + h - 8, w - 1, 6);
    ctx.fillStyle = RUST_LIGHT;
    ctx.fillRect(x + 1, y + h - 6, w - 2, 2.5);
  } else {
    const flH = Math.max(0.8, h * 0.28);
    const webY = y + flH;
    const webH = Math.max(0.8, h - 2 * flH);

    // Recessed web
    ctx.fillStyle = isBright ? '#242730' : '#13161c';
    ctx.fillRect(x, webY, w, webH);

    // Upper flange highlight
    ctx.fillStyle = STEEL_SPEC;
    ctx.fillRect(x, y, w, flH);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, w, 0.5);

    // Lower flange shadow
    ctx.fillStyle = isBright ? '#4a4d56' : '#23262d';
    ctx.fillRect(x, y + h - flH, w, flH);
    ctx.fillStyle = '#060910';
    ctx.fillRect(x, y + h - 0.4, w, 0.4);

    // Rust patina
    ctx.fillStyle = RUST_DARK;
    ctx.fillRect(x + 6, webY, w - 12, webH);
    ctx.fillStyle = RUST_MID;
    ctx.fillRect(x + 12, webY + 0.1, w * 0.4, Math.min(webH, 0.6));
    ctx.fillRect(x + w * 0.65, webY + 0.1, w * 0.25, Math.min(webH, 0.6));
  }
}

// Slanted 3D depth strut connecting front and rear frame coordinates with
// realistic flange lighting.
function draw3DStrut(ctx, x1, y1, x2, y2, isBright) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return;

  let nx = -dy / len;
  let ny = dx / len;
  if (ny > 0) { nx = -nx; ny = -ny; }

  const totalW = isBright ? 3.6 : 2.4;
  const flDist = isBright ? 1.2 : 0.8;

  // Dark profile contour
  ctx.strokeStyle = '#070a12';
  ctx.lineWidth = totalW;
  ctx.lineCap = 'square';
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Recessed body
  ctx.strokeStyle = isBright ? '#262932' : '#14161c';
  ctx.lineWidth = Math.max(1, totalW - 0.7);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Top flange edge
  const ox1 = x1 + nx * flDist, oy1 = y1 + ny * flDist;
  const ox2 = x2 + nx * flDist, oy2 = y2 + ny * flDist;

  ctx.strokeStyle = isBright ? STEEL_LIGHT : STEEL_MID;
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(ox1, oy1);
  ctx.lineTo(ox2, oy2);
  ctx.stroke();

  if (isBright) {
    ctx.strokeStyle = STEEL_SPEC;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(ox1, oy1);
    ctx.lineTo(ox2, oy2);
    ctx.stroke();
  }

  // Bottom flange edge
  const bx1 = x1 - nx * flDist, by1 = y1 - ny * flDist;
  const bx2 = x2 - nx * flDist, by2 = y2 - ny * flDist;

  ctx.strokeStyle = isBright ? STEEL_MID : STEEL_DARK;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(bx1, by1);
  ctx.lineTo(bx2, by2);
  ctx.stroke();

  if (isBright) {
    ctx.strokeStyle = RUST_MID;
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
}

// The horizontal centrifuge interior of the Invasion Capsule Sling module (V2 redesign):
// Grated catwalk, steel stairs with yellow nosing & yellow railing, horizontal rotating centrifuge
// casing with vertical cooling ribs, dark gunmetal tangential collar (#141b26 to #1e2533),
// deckplate in industrial steel (#728294), and angled accelerator tube with hydraulic strut.
function bakeCentrifugeInterior(ctx, fx, fy, fw, fh, dir) {
  const isWest = dir === 1;
  const cx = fx + (isWest ? fw * 0.44 : fw * 0.56) + dir * (MODULE_SKEW * 0.4);
  const drumY = fy + fh * 0.44;

  // 1. Grated catwalk at floor
  const catY = fy + fh - 8;
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(fx + 6, catY, fw - 12, 3.5);

  // 2. Steel stairs with yellow nosing
  for (let s = 0; s < 3; s++) {
    const stX = isWest ? (fx + 10 + s * 4.5) : (fx + fw - 18 - s * 4.5);
    const stY = catY - s * 5;
    ctx.fillStyle = '#64748b';
    ctx.fillRect(stX, stY, 8, 2);
    ctx.fillStyle = '#fde047';
    ctx.fillRect(stX, stY, 8, 0.75);
  }
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  if (isWest) {
    ctx.moveTo(fx + 10, catY - 4);
    ctx.lineTo(fx + 10 + 4.5 * 3, catY - 4 - 3 * 5);
  } else {
    ctx.moveTo(fx + fw - 10, catY - 4);
    ctx.lineTo(fx + fw - 10 - 4.5 * 3, catY - 4 - 3 * 5);
  }
  ctx.stroke();

  // 3. Yellow industrial railing
  ctx.strokeStyle = '#eab308';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let rx = fx + 10; rx < fx + fw - 12; rx += 9) {
    ctx.moveTo(rx, catY);
    ctx.lineTo(rx, catY - 5);
  }
  ctx.moveTo(fx + 10, catY - 5);
  ctx.lineTo(fx + fw - 14, catY - 5);
  ctx.stroke();

  // 4. Horizontal Centrifuge Turbine (parallel to ground plane)
  const rx = 20.0;
  const ry = 8.5;
  const cylH = 10.0;
  const topY = drumY - 3.0;
  const botY = topY + cylH;

  // Cylinder body shadow
  ctx.fillStyle = 'rgba(2, 6, 23, 0.45)';
  ctx.beginPath();
  ctx.ellipse(cx + dir * 2, botY + 3, rx * 0.9, ry * 0.65, 0, 0, Math.PI * 2);
  ctx.fill();

  // Cylinder wall
  ctx.fillStyle = '#1e2533';
  ctx.beginPath();
  ctx.moveTo(cx - rx, topY);
  ctx.lineTo(cx - rx, botY);
  ctx.ellipse(cx, botY, rx, ry, 0, 0, Math.PI, false);
  ctx.lineTo(cx + rx, topY);
  ctx.ellipse(cx, topY, rx, ry, 0, 0, Math.PI, true);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Cooling ribs
  [-0.7, -0.35, 0.0, 0.35, 0.7].forEach(ratio => {
    const lx = cx + rx * ratio;
    const lyTop = topY + Math.sqrt(Math.max(0, 1 - ratio * ratio)) * ry;
    const lyBot = botY + Math.sqrt(Math.max(0, 1 - ratio * ratio)) * ry;
    ctx.strokeStyle = '#0a0f18';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(lx, lyTop);
    ctx.lineTo(lx, lyBot);
    ctx.stroke();
    ctx.strokeStyle = ratio < 0 ? '#475569' : '#64748b';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(lx, lyTop);
    ctx.lineTo(lx, lyBot);
    ctx.stroke();
  });

  // Bottom rim
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.ellipse(cx, botY, rx, ry, 0, 0, Math.PI, false);
  ctx.stroke();

  // Horizontal deckplate in matching industrial steel (#728294)
  ctx.fillStyle = '#728294';
  ctx.beginPath();
  ctx.ellipse(cx, topY, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Highlight front rim
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.ellipse(cx, topY, rx - 0.8, ry - 0.5, 0, 0, Math.PI, false);
  ctx.stroke();

  // 8 Bolts
  for (let b = 0; b < 8; b++) {
    const ang = (b * Math.PI) / 4;
    const bx = cx + Math.cos(ang) * (rx - 2.2);
    const by = topY + Math.sin(ang) * (ry - 1.2);
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(bx - 0.8, by - 0.8, 1.6, 1.6);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(bx - 0.4, by - 0.4, 0.8, 0.8);
  }

  // Recessed rotor opening & stator coils
  const rimRx = rx - 4.5;
  const rimRy = ry - 2.2;
  ctx.fillStyle = '#070b14';
  ctx.beginPath();
  ctx.ellipse(cx, topY, rimRx, rimRy, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  for (let c = 0; c < 6; c++) {
    const cAng = (c * Math.PI) / 3;
    const coilX = cx + Math.cos(cAng) * (rimRx - 1.8);
    const coilY = topY + Math.sin(cAng) * (rimRy - 0.9);
    ctx.fillStyle = '#ca8a04';
    ctx.fillRect(coilX - 1.0, coilY - 0.7, 2.0, 1.4);
  }

  // 5. Monolithic accelerator tube & hydraulic strut
  const tubeStartX = cx + dir * 14.5;
  const tubeStartY = topY + cylH * 0.52;
  const tubeAngle = -0.50;
  const tubeR = 5.8;

  // External hydraulic strut
  const strutFootX = cx + dir * 12;
  const strutFootY = catY - 2;
  const strutMountX = tubeStartX + dir * (Math.cos(tubeAngle) * 16 - Math.sin(tubeAngle) * tubeR);
  const strutMountY = tubeStartY + (Math.sin(tubeAngle) * 16 + Math.cos(tubeAngle) * tubeR);

  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 3.8;
  ctx.beginPath();
  ctx.moveTo(strutFootX, strutFootY);
  ctx.lineTo(strutMountX, strutMountY);
  ctx.stroke();

  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(strutFootX, strutFootY);
  ctx.lineTo(strutMountX, strutMountY);
  ctx.stroke();

  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(strutFootX + (strutMountX - strutFootX) * 0.45, strutFootY + (strutMountY - strutFootY) * 0.45);
  ctx.lineTo(strutMountX, strutMountY);
  ctx.stroke();

  ctx.fillStyle = '#0a0f18';
  ctx.beginPath();
  ctx.arc(strutFootX, strutFootY, 2.5, 0, Math.PI * 2);
  ctx.arc(strutMountX, strutMountY, 2.2, 0, Math.PI * 2);
  ctx.fill();

  // Accelerator tube
  ctx.save();
  ctx.translate(tubeStartX, tubeStartY);
  ctx.scale(dir, 1);
  ctx.rotate(tubeAngle);

  // Tangential collar in dark gunmetal (#141b26 to #1e2533)
  const saddleBackX = -16;
  ctx.fillStyle = '#060a12';
  ctx.beginPath();
  ctx.moveTo(saddleBackX - 1, -tubeR - 4.5);
  ctx.lineTo(4, -tubeR - 2.5);
  ctx.lineTo(4, tubeR + 3.5);
  ctx.lineTo(saddleBackX - 1, tubeR + 4.5);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#141b26';
  ctx.beginPath();
  ctx.moveTo(saddleBackX, -tubeR - 3.5);
  ctx.lineTo(3.5, -tubeR - 1.8);
  ctx.lineTo(3.5, tubeR + 2.8);
  ctx.lineTo(saddleBackX, tubeR + 3.8);
  ctx.closePath();
  ctx.fill();

  // Tube body
  const tubeLen = 42;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(-1, -tubeR - 1, tubeLen + 2, tubeR * 2 + 2);
  ctx.fillStyle = '#334155';
  ctx.fillRect(0, -tubeR, tubeLen, tubeR * 2);
  ctx.fillStyle = '#475569';
  ctx.fillRect(0, -tubeR, tubeLen, 2.0);

  // Coil mount blocks (3 Marine indicator coils)
  for (let cIdx = 0; cIdx < 3; cIdx++) {
    const rx = COIL_OFFSETS[cIdx];
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(rx - 0.5, -tubeR - 2.5, 3.5, tubeR * 2 + 5);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(rx, -tubeR - 2, 2.5, tubeR * 2 + 4);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(rx, -tubeR - 2, 2.5, 0.8);
  }

  // Muzzle cap
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(tubeLen - 3, -tubeR - 1, 4, tubeR * 2 + 2);
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(tubeLen - 2.5, -tubeR - 0.5, 3, tubeR * 2 + 1);

  ctx.restore();
}

// The automated 3D assembly bay interior of the Drone Command module:
// Grated catwalk, steel safety railings, background spare drone rack,
// central hydraulic assembly cradle, and articulated robotic welding arms.
function bakeAssemblyBayInterior(ctx, fx, fy, fw, fh, dir) {
  const isWest = dir === 1;
  const cx = fx + (isWest ? fw * 0.46 : fw * 0.54) + dir * (MODULE_SKEW * 0.35);
  const cy = fy + fh * 0.42;
  const catY = fy + fh - 8;

  // 1. Grated catwalk at floor
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(fx + 6, catY, fw - 12, 3.5);
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(fx + 6, catY, fw - 12, 1.0);
  // Yellow hazard nosing
  ctx.fillStyle = '#eab308';
  ctx.fillRect(fx + 8, catY + 2.5, fw - 16, 1.0);

  // Industrial safety railing
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  for (let rx = fx + 10; rx < fx + fw - 12; rx += 10) {
    ctx.moveTo(rx, catY);
    ctx.lineTo(rx, catY - 4.5);
  }
  ctx.moveTo(fx + 10, catY - 4.5);
  ctx.lineTo(fx + fw - 12, catY - 4.5);
  ctx.stroke();

  // 2. Background drone frame storage rack
  const rackX = isWest ? (fx + 14) : (fx + fw - 28);
  const rackY = fy + 8;
  const rackW = 14;
  const rackH = 24;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(rackX, rackY, rackW, rackH);
  ctx.fillStyle = '#1e2530';
  ctx.fillRect(rackX + 1, rackY + 1, rackW - 2, rackH - 2);

  // Shelves with spare dark X-frames
  for (let s = 0; s < 3; s++) {
    const sy = rackY + 4 + s * 7;
    ctx.fillStyle = '#334155';
    ctx.fillRect(rackX + 1, sy, rackW - 2, 1.2);
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(rackX + 3, sy - 2);
    ctx.lineTo(rackX + rackW - 3, sy - 2);
    ctx.stroke();
  }

  // 3. Central Hydraulic Assembly Mount & Base Plate
  const basePlateW = 26;
  const basePlateH = 3.5;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(cx - basePlateW / 2 - 0.5, catY - 1, basePlateW + 1, basePlateH + 0.5);
  ctx.fillStyle = '#334155';
  ctx.fillRect(cx - basePlateW / 2, catY, basePlateW, basePlateH);
  ctx.fillStyle = STEEL_LIGHT;
  ctx.fillRect(cx - basePlateW / 2, catY, basePlateW, 0.8);

  // Hydraulic Lift Cylinder
  const cylW = 10;
  const cylTopY = cy + 4;
  const cylBotY = catY;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(cx - cylW / 2 - 0.5, cylTopY, cylW + 1, cylBotY - cylTopY);
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(cx - cylW / 2, cylTopY, cylW, cylBotY - cylTopY);
  // Chrome piston rod
  ctx.fillStyle = '#94a3b8';
  ctx.fillRect(cx - 2, cylTopY - 3, 4, 6);
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(cx - 1, cylTopY - 3, 1.2, 6);

  // Swivel cradle bed
  const bedW = 20;
  const bedH = 3.5;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(cx - bedW / 2 - 0.5, cylTopY - 4.5, bedW + 1, bedH + 1);
  ctx.fillStyle = '#475569';
  ctx.fillRect(cx - bedW / 2, cylTopY - 4, bedW, bedH);
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(cx - bedW / 2, cylTopY - 4, bedW, 0.7);

  // Clamp prongs
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(cx - bedW / 2 - 1, cylTopY - 7, 2.5, 4);
  ctx.fillRect(cx + bedW / 2 - 1.5, cylTopY - 7, 2.5, 4);
  ctx.fillStyle = '#ca8a04';
  ctx.fillRect(cx - bedW / 2 - 0.5, cylTopY - 6.5, 1.5, 3);
  ctx.fillRect(cx + bedW / 2 - 1, cylTopY - 6.5, 1.5, 3);

  // 4. Articulated Robotic Welding Arms (Static Bases & Upper Booms)
  const rob1X = cx - 15;
  const rob1Y = fy + 12;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(rob1X - 3.5, rob1Y - 2.5, 7, 5);
  ctx.fillStyle = '#334155';
  ctx.fillRect(rob1X - 3, rob1Y - 2, 6, 4);
  ctx.fillStyle = '#ca8a04';
  ctx.fillRect(rob1X - 1.5, rob1Y - 1, 3, 2);

  const rob2X = cx + 15;
  const rob2Y = fy + 12;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(rob2X - 3.5, rob2Y - 2.5, 7, 5);
  ctx.fillStyle = '#334155';
  ctx.fillRect(rob2X - 3, rob2Y - 2, 6, 4);
  ctx.fillStyle = '#ca8a04';
  ctx.fillRect(rob2X - 1.5, rob2Y - 1, 3, 2);

  // Upper boom arms
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(rob1X, rob1Y);
  ctx.lineTo(cx - 8, cy - 8);
  ctx.moveTo(rob2X, rob2Y);
  ctx.lineTo(cx + 8, cy - 8);
  ctx.stroke();

  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 2.0;
  ctx.beginPath();
  ctx.moveTo(rob1X, rob1Y);
  ctx.lineTo(cx - 8, cy - 8);
  ctx.moveTo(rob2X, rob2Y);
  ctx.lineTo(cx + 8, cy - 8);
  ctx.stroke();

  // Cable conduits
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(rob1X - 2, fy + 4);
  ctx.quadraticCurveTo(rob1X - 5, rob1Y - 2, rob1X, rob1Y);
  ctx.moveTo(rob2X + 2, fy + 4);
  ctx.quadraticCurveTo(rob2X + 5, rob2Y - 2, rob2X, rob2Y);
  ctx.stroke();

  // 5. Right Area: Industrial Luminaire, Relay Box & Heavy Cable Harness
  const rightX = fx + fw - 11;
  const lumY = fy + 5.5;

  // (A) Inspection Luminaire / Wall Floodlight
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(rightX - 1.0, lumY - 0.5, 6.5, 4.0);
  ctx.fillStyle = '#334155';
  ctx.fillRect(rightX - 0.5, lumY, 5.5, 3.0);
  // Halogen lens hood
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(rightX, lumY + 2.4, 4.5, 1.2);
  // Warm halogen light core
  ctx.fillStyle = '#fef08a';
  ctx.fillRect(rightX + 0.5, lumY + 2.2, 3.5, 1.2);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(rightX + 1.2, lumY + 2.4, 1.8, 0.8);

  // Soft downward light wash cone onto right wall
  ctx.fillStyle = 'rgba(254, 240, 138, 0.08)';
  ctx.beginPath();
  ctx.moveTo(rightX + 2.5, lumY + 3.5);
  ctx.lineTo(rightX - 6, catY);
  ctx.lineTo(rightX + 8, catY);
  ctx.closePath();
  ctx.fill();

  // (B) Control / Telemetry Relay Box
  const boxX = rightX - 2.0;
  const boxY = fy + 14;
  const boxW = 5.5;
  const boxH = 8.0;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(boxX - 0.5, boxY - 0.5, boxW + 1.0, boxH + 1.0);
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.fillStyle = '#334155';
  ctx.fillRect(boxX + 0.5, boxY + 0.5, boxW - 1.0, boxH - 1.0);
  // Yellow hazard stripe across junction box
  ctx.fillStyle = '#ca8a04';
  ctx.fillRect(boxX + 0.6, boxY + 1.2, boxW - 1.2, 1.0);
  // Online green LED indicator
  ctx.fillStyle = '#22c55e';
  ctx.fillRect(boxX + 1.0, boxY + 3.0, 1.3, 1.3);
  ctx.fillStyle = '#0f172a'; // Cable socket base
  ctx.fillRect(boxX + 0.8, boxY + boxH - 1.6, 3.8, 1.4);

  // (C) Cable Harness & Sagging Swag Loops
  // Vertical power conduit from ceiling into junction box
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(rightX + 2.0, fy + 2);
  ctx.lineTo(boxX + 2.5, boxY);
  ctx.stroke();

  // Flexible power feed to right robotic arm pedestal
  ctx.strokeStyle = '#1e293b';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(boxX, boxY + 3.5);
  ctx.quadraticCurveTo(boxX - 3, boxY + 6, rob2X + 2.5, rob2Y + 2);
  ctx.stroke();

  // Heavy sagging swag cables looping down into the floor
  // Black heavy conduit
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(boxX + 1.5, boxY + boxH);
  ctx.quadraticCurveTo(boxX + 4.0, catY - 5, rightX + 2.5, catY);
  ctx.stroke();

  // Orange industrial cable
  ctx.strokeStyle = '#ea580c';
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(boxX + 3.2, boxY + boxH);
  ctx.quadraticCurveTo(boxX + 0.5, catY - 4, rightX + 0.5, catY);
  ctx.stroke();

  // Cyan optic cable
  ctx.strokeStyle = '#06b6d4';
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(boxX + 4.2, boxY + boxH);
  ctx.quadraticCurveTo(boxX + 5.0, catY - 7, rightX + 4.0, catY);
  ctx.stroke();

  // Cable retaining clamp on wall
  ctx.fillStyle = '#64748b';
  ctx.fillRect(boxX + 0.5, catY - 4.5, 3.5, 1.0);
}

// The neoclassical US Congress Capitol Dome interior of the Congress Module (Variante B):
// 1. Plinth & ground terrace on the module platform in 2.5D perspective.
// 2. Rotunda drum (lower base with 3D elliptical colonnade & arched senate windows) below mid tie beam.
// 3. The Grand 2.5D Ribbed Capitol Dome with round elliptical base, directional neoclassical stone
//    shading (no bleached white), concentric masonry courses, and pedimented dormer windows.
// 4. Colonnaded cupola / tholos lantern on the dome crown with warm interior glow and stepped roof.
// 5. Holographic emitter collar on the pinnacle as mount for the animated globe.
function bakeDebatingChamberInterior(ctx, fx, fy, fw, fh, dir) {
  const isWest = dir === 1;
  const shiftX = dir * 6.2; // 2.5D depth centering: shifted in Z-depth into cage center
  const cx = fx + fw / 2 + shiftX;
  const catY = fy + fh - 11.5; // Centered on the 2.5D floor plane in Z-depth

  ctx.save();

  // 1. Plinth / Stepped 2.5D Terrace on floor
  const terrRx = 20.0;
  const terrRy = 6.0;
  const terrY = catY - 1.0;
  const terrH = 1.8;

  // Terrace drop shadow on floor
  ctx.fillStyle = 'rgba(2, 6, 23, 0.6)';
  ctx.beginPath();
  ctx.ellipse(cx, terrY + terrH + 1, terrRx + 2, terrRy + 1, 0, 0, Math.PI * 2);
  ctx.fill();

  // Terrace riser (front curved cylinder side)
  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.ellipse(cx, terrY + terrH, terrRx, terrRy, 0, 0, Math.PI, false);
  ctx.ellipse(cx, terrY, terrRx, terrRy, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  // Terrace top deck
  ctx.fillStyle = isWest ? '#64748b' : '#475569';
  ctx.beginPath();
  ctx.ellipse(cx, terrY, terrRx, terrRy, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Level B: Stepped Marble Stylobate (Podium for Columns)
  const styloRx = 17.5;
  const styloRy = 5.0;
  const styloH = 1.6;
  const styloY = terrY - styloH;

  // Stylobate riser
  ctx.fillStyle = '#334155';
  ctx.beginPath();
  ctx.ellipse(cx, styloY + styloH, styloRx, styloRy, 0, 0, Math.PI, false);
  ctx.ellipse(cx, styloY, styloRx, styloRy, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  // Stylobate marble deck
  const styloGrad = ctx.createLinearGradient(cx - styloRx, styloY, cx + styloRx, styloY);
  if (isWest) {
    styloGrad.addColorStop(0, '#94a3b8');
    styloGrad.addColorStop(0.35, '#cbd5e1');
    styloGrad.addColorStop(1, '#64748b');
  } else {
    styloGrad.addColorStop(0, '#64748b');
    styloGrad.addColorStop(0.65, '#cbd5e1');
    styloGrad.addColorStop(1, '#94a3b8');
  }
  ctx.fillStyle = styloGrad;
  ctx.beginPath();
  ctx.ellipse(cx, styloY, styloRx, styloRy, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 0.7;
  ctx.stroke();

  // 2. Rotunda Drum (Peristyle Colonnade in 2.5D Perspective)
  const drumRx = 15.0;
  const drumRy = 4.2;
  const drumBotY = styloY - 0.2;
  const drumTopY = fy + 25.2;

  // Recessed interior cylindrical shadow
  ctx.fillStyle = '#080d16';
  ctx.beginPath();
  ctx.ellipse(cx, drumBotY, drumRx - 1, drumRy - 0.5, 0, 0, Math.PI, false);
  ctx.ellipse(cx, drumTopY, drumRx - 1, drumRy - 0.5, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  // Drum back wall stone
  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.ellipse(cx, drumBotY, drumRx - 1.5, drumRy - 0.8, 0, 0, Math.PI, false);
  ctx.ellipse(cx, drumTopY, drumRx - 1.5, drumRy - 0.8, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  // Warm Amber Senate Windows & 3D Columns
  const numCols = 9;
  for (let i = 0; i < numCols; i++) {
    const ang = Math.PI * (0.08 + (i / (numCols - 1)) * 0.84);
    const colX = cx - Math.cos(ang) * drumRx;
    const colCurveY = Math.sin(ang) * drumRy;
    const colY1 = drumTopY + colCurveY;
    const colY2 = drumBotY + colCurveY;

    // Window in the niche between columns
    if (i < numCols - 1) {
      const nextAng = Math.PI * (0.08 + ((i + 1) / (numCols - 1)) * 0.84);
      const nextColX = cx - Math.cos(nextAng) * drumRx;
      const midAng = (ang + nextAng) / 2;
      const winX = cx - Math.cos(midAng) * (drumRx - 1.5);
      const winCurveY = Math.sin(midAng) * (drumRy - 0.6);
      const winY1 = drumTopY + winCurveY + 1.8;
      const winY2 = drumBotY + winCurveY - 1.4;
      const winW = Math.max(1.0, Math.abs(nextColX - colX) * 0.55);
      const winH = winY2 - winY1;

      if (winH > 0) {
        // Dark window niche
        ctx.fillStyle = '#060a12';
        ctx.fillRect(winX - winW / 2 - 0.4, winY1, winW + 0.8, winH);

        // Warm glowing amber senate glass
        ctx.fillStyle = '#d97706';
        ctx.fillRect(winX - winW / 2, winY1 + 0.4, winW, winH - 0.4);

        // Arched top of window
        ctx.fillStyle = '#f59e0b';
        ctx.beginPath();
        ctx.ellipse(winX, winY1 + 1.0, winW / 2, 1.0, 0, 0, Math.PI * 2);
        ctx.fill();

        // Golden incandescent inner glow
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(winX - winW * 0.25, winY1 + 0.8, winW * 0.5, winH * 0.45);
      }
    }

    // 3D Fluted Marble Column
    const colW = 1.2;
    const colH = colY2 - colY1;
    if (colH > 0) {
      ctx.fillStyle = '#334155';
      ctx.fillRect(colX - colW / 2 - 0.3, colY1, colW + 0.6, colH);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(colX - colW / 2, colY1, colW, colH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(colX - colW / 2 + (isWest ? 0.2 : 0.4), colY1, 0.5, colH);

      // Capital & Base torus
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(colX - colW / 2 - 0.3, colY1 - 0.3, colW + 0.6, 0.8);
      ctx.fillRect(colX - colW / 2 - 0.3, colY2 - 0.6, colW + 0.6, 0.8);
    }
  }

  // Entablature & Cornice capping the Drum (2.5D Ellipse)
  const corniceRx = drumRx + 1.2;
  const corniceRy = drumRy + 0.4;
  const corniceY = drumTopY;

  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.ellipse(cx, corniceY + 1.0, corniceRx, corniceRy, 0, 0, Math.PI, false);
  ctx.ellipse(cx, corniceY, corniceRx, corniceRy, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#e2e8f0';
  ctx.beginPath();
  ctx.ellipse(cx, corniceY, corniceRx, corniceRy, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 0.7;
  ctx.stroke();

  // 3. Attic Story (Podium between Drum and Dome, passing behind mid tie beam)
  const atticRx = 14.0;
  const atticRy = 3.8;
  const atticY = corniceY - 0.8;
  const domeBaseY = atticY - 1.8;

  ctx.fillStyle = '#475569';
  ctx.beginPath();
  ctx.ellipse(cx, atticY, atticRx, atticRy, 0, 0, Math.PI, false);
  ctx.ellipse(cx, domeBaseY, atticRx, atticRy, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  for (let p = -10; p <= 10; p += 4.5) {
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(cx + p - 0.3, domeBaseY + 0.3, 0.6, 1.4);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(cx + p + 0.3, domeBaseY + 0.3, 0.4, 1.4);
  }

  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.ellipse(cx, domeBaseY, atticRx + 0.3, atticRy + 0.2, 0, 0, Math.PI, false);
  ctx.stroke();

  // 4. The Grand Ribbed Capitol Dome (2.5D Volumetric Dome with Round Base & Neoclassical Shading)
  const domeRx = 13.0;
  const domeRy = 3.4;
  const domeCrownY = fy + 15.4;
  const domeH = domeBaseY - domeCrownY;

  // Trace 2.5D Volumetric Dome Contour (Round elliptical base!)
  const traceDomeBody = (c) => {
    c.beginPath();
    c.moveTo(cx - domeRx, domeBaseY);
    c.bezierCurveTo(
      cx - domeRx * 1.02, domeCrownY + domeH * 0.55,
      cx - domeRx * 0.45, domeCrownY,
      cx, domeCrownY
    );
    c.bezierCurveTo(
      cx + domeRx * 0.45, domeCrownY,
      cx + domeRx * 1.02, domeCrownY + domeH * 0.55,
      cx + domeRx, domeBaseY
    );
    // ROUND 2.5D BASE: Curves DOWNWARD in front through (cx, domeBaseY + domeRy)!
    c.ellipse(cx, domeBaseY, domeRx, domeRy, 0, 0, Math.PI, false);
    c.closePath();
  };

  // Dark drop shadow silhouette / contour outline
  ctx.save();
  ctx.lineWidth = 1.8;
  ctx.strokeStyle = '#070b14';
  traceDomeBody(ctx);
  ctx.stroke();
  ctx.restore();

  // Rich Neoclassical Stone Shading (directional volumetric lighting)
  const domeGrad = ctx.createLinearGradient(cx - domeRx, domeCrownY, cx + domeRx, domeCrownY);
  if (isWest) {
    domeGrad.addColorStop(0.0, '#54667a');
    domeGrad.addColorStop(0.20, '#8fa1b5');
    domeGrad.addColorStop(0.42, '#f1f5f9');
    domeGrad.addColorStop(0.58, '#e2e8f0');
    domeGrad.addColorStop(0.85, '#94a3b8');
    domeGrad.addColorStop(1.0, '#475569');
  } else {
    domeGrad.addColorStop(0.0, '#475569');
    domeGrad.addColorStop(0.15, '#94a3b8');
    domeGrad.addColorStop(0.42, '#e2e8f0');
    domeGrad.addColorStop(0.58, '#f1f5f9');
    domeGrad.addColorStop(0.80, '#8fa1b5');
    domeGrad.addColorStop(1.0, '#54667a');
  }

  traceDomeBody(ctx);
  ctx.fillStyle = domeGrad;
  ctx.fill();

  // Ambient Occlusion shadow at base rim
  ctx.save();
  traceDomeBody(ctx);
  ctx.clip();
  const aoGrad = ctx.createLinearGradient(cx, domeBaseY - 2.5, cx, domeBaseY + domeRy);
  aoGrad.addColorStop(0, 'rgba(15, 23, 42, 0)');
  aoGrad.addColorStop(1, 'rgba(15, 23, 42, 0.45)');
  ctx.fillStyle = aoGrad;
  ctx.fill();
  ctx.restore();

  // Concentric 2.5D Masonry Courses (Horizontal Stone Tiers)
  const t1Y = domeBaseY - domeH * 0.35;
  const t1Rx = domeRx * 0.94;
  const t1Ry = domeRy * 0.94;
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.ellipse(cx, t1Y, t1Rx, t1Ry, 0, 0, Math.PI, false);
  ctx.stroke();
  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 0.4;
  ctx.beginPath();
  ctx.ellipse(cx, t1Y - 0.4, t1Rx, t1Ry, 0, 0, Math.PI, false);
  ctx.stroke();

  const t2Y = domeBaseY - domeH * 0.70;
  const t2Rx = domeRx * 0.70;
  const t2Ry = domeRy * 0.70;
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.ellipse(cx, t2Y, t2Rx, t2Ry, 0, 0, Math.PI, false);
  ctx.stroke();
  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 0.4;
  ctx.beginPath();
  ctx.ellipse(cx, t2Y - 0.4, t2Rx, t2Ry, 0, 0, Math.PI, false);
  ctx.stroke();

  // Ring of 7 Classical Pedimented Dormer Windows (Gaubenfenster)
  const numDormers = 7;
  for (let d = 0; d < numDormers; d++) {
    const dAng = Math.PI * (0.12 + (d / (numDormers - 1)) * 0.76);
    const dX = cx - Math.cos(dAng) * (domeRx * 0.84);
    const dCurveY = (domeBaseY - domeH * 0.22) + Math.sin(dAng) * (domeRy * 0.84);

    // Gabled / Pedimented stone hood
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.moveTo(dX - 1.8, dCurveY - 1.0);
    ctx.lineTo(dX + 1.8, dCurveY - 1.0);
    ctx.lineTo(dX, dCurveY - 2.6);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#cbd5e1';
    ctx.beginPath();
    ctx.moveTo(dX - 1.4, dCurveY - 1.1);
    ctx.lineTo(dX + 1.4, dCurveY - 1.1);
    ctx.lineTo(dX, dCurveY - 2.3);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(dX - (isWest ? 1.4 : 0), dCurveY - 1.1);
    ctx.lineTo(dX, dCurveY - 2.3);
    ctx.lineTo(dX + (isWest ? 0 : 1.4), dCurveY - 1.1);
    ctx.closePath();
    ctx.fill();

    // Arched dark window casing
    ctx.fillStyle = '#090d16';
    ctx.beginPath();
    ctx.ellipse(dX, dCurveY, 1.2, 1.6, 0, 0, Math.PI * 2);
    ctx.fill();

    // Warm amber glowing window glass
    ctx.fillStyle = '#d97706';
    ctx.beginPath();
    ctx.ellipse(dX, dCurveY + 0.2, 0.9, 1.2, 0, 0, Math.PI * 2);
    ctx.fill();

    // Bright incandescent center
    ctx.fillStyle = '#fef08a';
    ctx.fillRect(dX - 0.3, dCurveY - 0.5, 0.6, 0.8);
  }

  // Volumetric 3D Architectural Ribs (Rippen)
  const numRibs = 9;
  for (let r = 0; r < numRibs; r++) {
    const rAng = Math.PI * (0.07 + (r / (numRibs - 1)) * 0.86);
    const startX = cx - Math.cos(rAng) * (domeRx + 0.2);
    const startY = domeBaseY + Math.sin(rAng) * domeRy;
    const endX = cx - Math.cos(rAng) * 2.0;
    const endY = domeCrownY;
    const ctrlX = cx - Math.cos(rAng) * (domeRx * 0.60);
    const ctrlY = domeCrownY + domeH * 0.38 + Math.sin(rAng) * (domeRy * 0.60);

    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
    ctx.stroke();

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(startX - 0.2, startY);
    ctx.quadraticCurveTo(ctrlX - 0.2, ctrlY, endX - 0.1, endY);
    ctx.stroke();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(startX + (isWest ? -0.3 : 0.3), startY);
    ctx.quadraticCurveTo(ctrlX + (isWest ? -0.3 : 0.3), ctrlY, endX, endY);
    ctx.stroke();
  }

  // Specular sheen sweep across the sunlit dome shoulder
  const specX = isWest ? (cx - domeRx * 0.35) : (cx + domeRx * 0.35);
  ctx.save();
  traceDomeBody(ctx);
  ctx.clip();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.beginPath();
  ctx.ellipse(specX, domeCrownY + domeH * 0.35, 3.0, 5.5, isWest ? -0.2 : 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // 5. The Colonnaded Cupola / Tholos Lantern (Crown of the Dome)
  const lanternRx = 5.2;
  const lanternRy = 1.4;
  const lanternBaseY = domeCrownY;
  const lanternRoofY = lanternBaseY - 2.8;

  ctx.fillStyle = '#0a0f18';
  ctx.beginPath();
  ctx.ellipse(cx, lanternBaseY + 0.8, lanternRx + 0.5, lanternRy + 0.3, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#e2e8f0';
  ctx.beginPath();
  ctx.ellipse(cx, lanternBaseY, lanternRx, lanternRy, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 0.6;
  ctx.stroke();

  // Tholos Colonnade (Miniature Columns & Warm Interior Glow)
  ctx.fillStyle = '#090d16';
  ctx.beginPath();
  ctx.ellipse(cx, lanternBaseY - 0.3, lanternRx - 1.0, lanternRy - 0.3, 0, 0, Math.PI, false);
  ctx.ellipse(cx, lanternRoofY + 0.8, lanternRx - 1.0, lanternRy - 0.3, 0, Math.PI, 0, true);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#f59e0b';
  ctx.beginPath();
  ctx.ellipse(cx, (lanternBaseY + lanternRoofY) / 2, lanternRx - 1.8, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#fef08a';
  ctx.fillRect(cx - 1.0, (lanternBaseY + lanternRoofY) / 2 - 0.8, 2.0, 1.6);

  // 5 Miniature White Marble Pillars
  const numPillars = 5;
  for (let p = 0; p < numPillars; p++) {
    const pAng = Math.PI * (0.12 + (p / (numPillars - 1)) * 0.76);
    const px = cx - Math.cos(pAng) * (lanternRx - 0.8);
    const py1 = lanternRoofY + 0.6 + Math.sin(pAng) * (lanternRy - 0.3);
    const py2 = lanternBaseY - 0.2 + Math.sin(pAng) * (lanternRy - 0.3);
    const pH = py2 - py1;

    if (pH > 0) {
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(px - 0.3, py1, 0.6, pH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px - 0.2, py1, 0.4, pH);
    }
  }

  // Stepped Conical Roof / Dome Cap of Lantern
  const roofApexY = lanternRoofY - 1.8;

  ctx.fillStyle = '#0a0f18';
  ctx.beginPath();
  ctx.moveTo(cx - (lanternRx - 0.4), lanternRoofY + 0.6);
  ctx.lineTo(cx + (lanternRx - 0.4), lanternRoofY + 0.6);
  ctx.lineTo(cx, roofApexY - 0.4);
  ctx.closePath();
  ctx.fill();

  const roofGrad = ctx.createLinearGradient(cx - lanternRx, lanternRoofY, cx + lanternRx, lanternRoofY);
  if (isWest) {
    roofGrad.addColorStop(0, '#94a3b8');
    roofGrad.addColorStop(0.4, '#ffffff');
    roofGrad.addColorStop(1, '#475569');
  } else {
    roofGrad.addColorStop(0, '#475569');
    roofGrad.addColorStop(0.6, '#ffffff');
    roofGrad.addColorStop(1, '#94a3b8');
  }
  ctx.fillStyle = roofGrad;
  ctx.beginPath();
  ctx.moveTo(cx - (lanternRx - 0.6), lanternRoofY + 0.5);
  ctx.lineTo(cx + (lanternRx - 0.6), lanternRoofY + 0.5);
  ctx.lineTo(cx, roofApexY);
  ctx.closePath();
  ctx.fill();

  // 6. Holographic Emitter Collar (Mount for the Globe)
  const emitterY = roofApexY - 1.2;
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(cx - 1.6, emitterY, 3.2, 1.2);
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(cx - 1.0, emitterY + 0.2, 2.0, 0.5);
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(cx - 0.5, emitterY + 0.2, 1.0, 0.3);

  ctx.restore();
}

// Generic filler interior for modules that have not defined their own yet.
function bakeGenericInterior(ctx, fx, fy, fw, fh) {
  const floorY = fy + fh - 8;
  drawIBeam(ctx, fx + 8, floorY - 3, fw - 16, 3, false, false);
  ctx.fillStyle = '#1e2530';
  ctx.fillRect(fx + fw * 0.3, floorY - 22, fw * 0.4, 20);
  ctx.fillStyle = '#334155';
  ctx.fillRect(fx + fw * 0.3 + 2, floorY - 20, fw * 0.4 - 4, 16);
  ctx.fillStyle = STEEL_LIGHT;
  ctx.fillRect(fx + fw * 0.3 + 2, floorY - 20, fw * 0.4 - 4, 1.2);
}

// Bakes one module's complete static geometry into an offscreen canvas.
// Returns the canvas plus placing offsets that centre the front face horizontally
// at getSlotX() and align the bottom edge at getSlotY() + MODULE_H / 2.
function buildModuleBitmap(moduleId, team) {
  const key = `${moduleId}|${team}`;
  const cached = moduleBitmapCache.get(key);
  if (cached) return cached;

  const dir = team === 'western' ? 1 : -1;
  const isWest = dir === 1;
  const bw = 120;
  const bh = 84;

  const canvas = document.createElement('canvas');
  canvas.width = bw;
  canvas.height = bh;
  const ctx = canvas.getContext('2d');

  const fw = MODULE_W;
  const fh = MODULE_FRONT_H;
  const fx = isWest ? 4 : 22;
  const fy = MODULE_TOP_D + 2; // 25 (front face bottom at 66 <= bh 72)

  const extraSkew = 2.5;
  const shadowSkewX = MODULE_SKEW + extraSkew;
  const bx = fx + dir * MODULE_SKEW;
  const by = fy - MODULE_TOP_D; // 2

  const T = 4.2; // Double-T I-beam thickness

  const shadowFrontX = isWest ? fx + T / 2 : fx + fw - T / 2;
  const shadowBackX = isWest ? (fx + dir * shadowSkewX) + T / 2 : (fx + fw + dir * shadowSkewX) - T / 2;

  // 1. Rear frame
  const leftRearColX = isWest ? (shadowBackX - 2.5) : bx;
  const rightRearColX = isWest ? (bx + fw - 4) : (shadowBackX - 2.5);
  const rearBeamX = isWest ? shadowBackX : leftRearColX;
  const rearBeamW = isWest ? ((bx + fw) - rearBeamX) : (shadowBackX - rearBeamX);

  ctx.fillStyle = STEEL_DARK;
  ctx.fillRect(rearBeamX, by + fh - 4.5, rearBeamW, 4.5);
  ctx.fillRect(leftRearColX, by, 3.8, fh);
  ctx.fillRect(rightRearColX, by, 3.8, fh);

  // Visible rear column towards arena
  const rearVisColX = isWest ? bx + fw - 4 : bx;
  ctx.fillStyle = STEEL_LIGHT;
  ctx.fillRect(rearVisColX, by, 3.8, fh);
  ctx.fillStyle = STEEL_SPEC;
  ctx.fillRect(rearVisColX + (isWest ? 2.5 : 0), by, 1.2, fh);

  // Upper rear beam (Roof Beam Heck - fully illuminated)
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(rearBeamX - 1, by - 1, rearBeamW + 2, 6);
  ctx.fillStyle = STEEL_LIGHT;
  ctx.fillRect(rearBeamX, by, rearBeamW, 4.5);
  ctx.fillStyle = STEEL_SPEC;
  ctx.fillRect(rearBeamX, by, rearBeamW, 1.5);

  // 2. Interior machinery
  const def = MODULE_DEFS[moduleId];
  if (def?.interior === 'centrifuge_on_girders') {
    bakeCentrifugeInterior(ctx, fx, fy, fw, fh, dir);
  } else if (def?.interior === 'assembly_bay') {
    bakeAssemblyBayInterior(ctx, fx, fy, fw, fh, dir);
  } else if (def?.interior === 'debating_chamber') {
    bakeDebatingChamberInterior(ctx, fx, fy, fw, fh, dir);
  } else if (def) {
    bakeGenericInterior(ctx, fx, fy, fw, fh);
  }

  // 3. 3D depth struts receding into depth
  const arenaFrontX = isWest ? fx + fw - T / 2 : fx + T / 2;
  const arenaBackX = isWest ? bx + fw - T / 2 : bx + T / 2;

  // Shadow side struts
  draw3DStrut(ctx, shadowFrontX, fy + T / 2, shadowBackX, by, true);
  draw3DStrut(ctx, shadowFrontX, fy + fh - T / 2, shadowBackX, by + fh - 2, false);

  // Arena flank struts (clean parallel horizontal structure matching front wall)
  draw3DStrut(ctx, arenaFrontX, fy + T / 2, arenaBackX, by + T / 2, true);
  draw3DStrut(ctx, arenaFrontX, fy + fh - T / 2, arenaBackX, by + fh - T / 2, true);
  draw3DStrut(ctx, arenaFrontX, fy + fh * 0.52, arenaBackX, by + fh * 0.52, true);

  // 4. Front I-beam frame
  drawIBeam(ctx, fx, fy, fw, T, false, true);                   // Top chord
  drawIBeam(ctx, fx, fy + fh - T, fw, T, false, true);          // Bottom chord
  drawIBeam(ctx, fx, fy, T, fh, true, !isWest);                // Left column
  drawIBeam(ctx, fx + fw - T, fy, T, fh, true, isWest);         // Right column
  drawIBeam(ctx, fx, fy + fh * 0.52, fw, 2.6, false, true);     // Mid tie beam

  // Rivet rows along front columns
  for (let ry = fy + 13; ry < fy + fh - 13; ry += 8) {
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(fx + 1.5, ry, 1.1, 1.1);
    ctx.fillRect(fx + fw - 2.6, ry, 1.1, 1.1);
  }

  // 5. 8x8 Gusset plates with rivets (4 corners + 2 mid-height)
  const gSize = 8;
  const corners = [
    fx, fy,
    fx + fw - gSize, fy,
    fx, fy + fh - gSize,
    fx + fw - gSize, fy + fh - gSize,
    fx, fy + fh * 0.52 - gSize / 2,
    fx + fw - gSize, fy + fh * 0.52 - gSize / 2
  ];
  for (let c = 0; c < corners.length; c += 2) {
    const cx = corners[c];
    const cy = corners[c + 1];
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(cx, cy, gSize, gSize);
    ctx.fillStyle = STEEL_LIGHT;
    ctx.fillRect(cx + 0.6, cy + 0.6, gSize - 1.2, gSize - 1.2);
    ctx.fillStyle = RUST_MID;
    ctx.fillRect(cx + 1, cy + 1, 2, 2);
    ctx.fillRect(cx + gSize - 3, cy + gSize - 3, 2, 2);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(cx + 1.2, cy + 1.2, 1.2, 1.2);
    ctx.fillRect(cx + gSize - 2.5, cy + gSize - 2.5, 1.2, 1.2);
  }

  // Rear column mounting pads
  const rearGussetX = isWest ? bx + fw - 3.5 : bx + 1;
  const rearGys = [by, by + fh * 0.52 - 2, by + fh - 5];
  for (let g = 0; g < rearGys.length; g++) {
    const gy = rearGys[g];
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(rearGussetX - 0.5, gy - 0.5, 3.5, 3.5);
    ctx.fillStyle = STEEL_LIGHT;
    ctx.fillRect(rearGussetX, gy, 2.5, 2.5);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(rearGussetX + 0.6, gy + 0.6, 1.0, 1.0);
  }

  // 6. Flush lower foot pads
  const footXs = [fx, fx + fw - T];
  for (let f = 0; f < 2; f++) {
    const px = footXs[f];
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(px - 0.5, fy + fh - 2.5, T + 1, 2.5);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(px, fy + fh - 2, T, 2);
  }

  // Docking status LEDs on bottom corners (green active bus)
  const ledXs = [fx + 1.5, fx + fw - T + 1.2];
  for (let l = 0; l < 2; l++) {
    const lx = ledXs[l];
    ctx.fillStyle = '#052e16';
    ctx.fillRect(lx - 0.5, fy + fh - 3.5, 2.5, 2.5);
    ctx.fillStyle = '#22c55e';
    ctx.fillRect(lx, fy + fh - 3, 1.5, 1.5);
    ctx.fillStyle = '#86efac';
    ctx.fillRect(lx + 0.3, fy + fh - 2.7, 0.8, 0.8);
  }

  const entry = {
    canvas,
    w: bw,
    h: bh,
    offX: -(fx + fw / 2),
    offY: -((fy + fh) - MODULE_H / 2)
  };
  moduleBitmapCache.set(key, entry);
  return entry;
}

// Bakes the static geometry of the Industrial Apex Roof Superstructure:
// Armored plinth collar, 3D top deck with anti-slip grooves & D-rings,
// HVAC generator with louver vents & twin exhaust stacks, radar dome base,
// and avionics lattice antenna mast with dipoles.
function buildCapBitmap(team) {
  const key = `__cap|${team}`;
  const cached = moduleBitmapCache.get(key);
  if (cached) return cached;

  const dir = team === 'western' ? 1 : -1;
  const isWest = dir === 1;
  const bw = CAP_W;
  const bh = CAP_H;

  const canvas = document.createElement('canvas');
  canvas.width = bw;
  canvas.height = bh;
  const ctx = canvas.getContext('2d');

  const W = MODULE_W;
  const extraSkew = 2.5;
  const shadowSkewX = MODULE_SKEW + extraSkew;
  const T = 5.2;

  const teamGlow = team === 'western' ? '#00f0ff' : '#ff0055';
  const deckBase = team === 'western' ? '#1e293b' : '#3f1307';
  const deckLight = team === 'western' ? '#64748b' : '#9a3412';
  const frontMid = team === 'western' ? '#334155' : '#6b250e';
  const frontDark = team === 'western' ? '#1a2230' : '#2d0c05';
  const sideDark = team === 'western' ? '#0f172a' : '#1c0702';
  const highlight = team === 'western' ? '#94a3b8' : '#ea580c';
  const specLine = '#f8fafc';

  const fx = isWest ? 4 : 22;
  const fy = 68;
  const by = fy - MODULE_TOP_D;
  const shadowBackX = isWest ? (fx + dir * shadowSkewX) + T / 2 : (fx + W + dir * shadowSkewX) - T / 2;
  const arenaBackX = isWest ? (fx + dir * MODULE_SKEW) + W - T / 2 : (fx + dir * MODULE_SKEW) + T / 2;

  // 1. Armored plinth collar
  const collarH = 7;
  const collarY = fy - collarH;
  const collarRearY = by - collarH;

  // Front Fascia
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(fx - 1, collarY - 1, W + 2, collarH + 2);
  ctx.fillStyle = frontMid;
  ctx.fillRect(fx, collarY, W, collarH);

  ctx.fillStyle = specLine;
  ctx.fillRect(fx, collarY, W, 1.2);

  // Recessed team-glow channel
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(fx + 6, collarY + 3, W - 12, 2);
  ctx.fillStyle = teamGlow;
  ctx.fillRect(fx + 7, collarY + 3.4, W - 14, 1.2);

  // Corner anchor pads
  const anchorXs = [fx, fx + W - 10];
  for (let a = 0; a < 2; a++) {
    const cx = anchorXs[a];
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(cx - 0.5, collarY, 11, collarH);
    ctx.fillStyle = deckLight;
    ctx.fillRect(cx, collarY + 0.5, 10, collarH - 1);
    ctx.fillStyle = specLine;
    ctx.fillRect(cx + 2, collarY + 2, 1.5, 1.5);
    ctx.fillRect(cx + 6.5, collarY + 2, 1.5, 1.5);
  }

  // 3D side flank
  const flankCollarX = isWest ? fx + W : fx;
  const flankRearX = arenaBackX;

  ctx.fillStyle = sideDark;
  ctx.beginPath();
  ctx.moveTo(flankCollarX, collarY);
  ctx.lineTo(flankRearX, collarRearY);
  ctx.lineTo(flankRearX, by);
  ctx.lineTo(flankCollarX, fy);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.strokeStyle = highlight;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(flankCollarX, collarY);
  ctx.lineTo(flankRearX, collarRearY);
  ctx.stroke();

  // Top deck
  ctx.fillStyle = deckBase;
  ctx.beginPath();
  ctx.moveTo(fx, collarY);
  ctx.lineTo(fx + W, collarY);
  ctx.lineTo(arenaBackX, collarRearY);
  ctx.lineTo(shadowBackX, collarRearY);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Hazard stripe
  ctx.strokeStyle = '#eab308';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(fx + 4, collarY - 2);
  ctx.lineTo(fx + W - 4, collarY - 2);
  ctx.stroke();

  // Anti-slip grooves
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 0.8;
  for (let dy = collarY - 4; dy > collarRearY + 3; dy -= 4) {
    const rat = (collarY - dy) / MODULE_TOP_D;
    const x1 = fx + dir * (rat * MODULE_SKEW) + 8;
    const x2 = fx + W + dir * (rat * MODULE_SKEW) - 8;
    ctx.beginPath();
    ctx.moveTo(x1, dy);
    ctx.lineTo(x2, dy);
    ctx.stroke();
  }

  // Crane D-rings
  const padCorners = [
    fx + 7, collarY - 2,
    fx + W - 7, collarY - 2,
    shadowBackX + (isWest ? 4 : -4), collarRearY + 3,
    arenaBackX + (isWest ? -4 : 4), collarRearY + 3
  ];
  for (let p = 0; p < padCorners.length; p += 2) {
    const px = padCorners[p];
    const py = padCorners[p + 1];
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.arc(px, py, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#cbd5e1';
    ctx.beginPath();
    ctx.arc(px, py, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.arc(px, py, 0.8, 0, Math.PI * 2);
    ctx.fill();
  }

  // 2. HVAC Generator
  const genW = 28;
  const genH = 16;
  const genDepth = 14;
  const genSkew = MODULE_SKEW * (genDepth / MODULE_TOP_D);
  const genX = isWest ? fx + 6 : fx + W - genW - 6;
  const genY = collarY - 3;
  const genTopY = genY - genH;

  // Generator drop shadow
  ctx.fillStyle = 'rgba(2, 6, 23, 0.6)';
  ctx.beginPath();
  ctx.moveTo(genX - 2, genY + 1);
  ctx.lineTo(genX + genW + 4, genY + 1);
  ctx.lineTo(genX + genW + 4 + dir * (genSkew * 0.8), genY - genDepth * 0.8);
  ctx.lineTo(genX - 2 + dir * (genSkew * 0.8), genY - genDepth * 0.8);
  ctx.closePath();
  ctx.fill();

  // Generator front
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(genX - 1, genTopY - 1, genW + 2, genH + 2);
  ctx.fillStyle = frontDark;
  ctx.fillRect(genX, genTopY, genW, genH);

  // Generator roof
  ctx.fillStyle = frontMid;
  ctx.beginPath();
  ctx.moveTo(genX, genTopY);
  ctx.lineTo(genX + genW, genTopY);
  ctx.lineTo(genX + genW + dir * genSkew, genTopY - genDepth);
  ctx.lineTo(genX + dir * genSkew, genTopY - genDepth);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = specLine;
  ctx.fillRect(genX, genTopY, genW, 1);

  // Generator flank
  const gFlankX = isWest ? genX + genW : genX;
  ctx.fillStyle = sideDark;
  ctx.beginPath();
  ctx.moveTo(gFlankX, genTopY);
  ctx.lineTo(gFlankX + dir * genSkew, genTopY - genDepth);
  ctx.lineTo(gFlankX + dir * genSkew, genY - genDepth);
  ctx.lineTo(gFlankX, genY);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Louver slats
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(genX + 3, genTopY + 3, genW - 6, genH - 6);
  ctx.fillStyle = teamGlow;
  ctx.globalAlpha = 0.4;
  ctx.fillRect(genX + 4, genTopY + 4, genW - 8, genH - 8);
  ctx.globalAlpha = 1.0;
  for (let ly = genTopY + 4; ly < genTopY + genH - 4; ly += 2.5) {
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(genX + 4, ly, genW - 8, 1.5);
    ctx.fillStyle = '#64748b';
    ctx.fillRect(genX + 4, ly, genW - 8, 0.5);
  }

  // Twin exhaust stacks
  const stackXs = [genX + 7, genX + genW - 9];
  for (let s = 0; s < 2; s++) {
    const px = stackXs[s];
    const py = genTopY - genDepth * 0.45;
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.ellipse(px + dir * 2, py, 3.5, 1.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#cbd5e1';
    ctx.beginPath();
    ctx.ellipse(px + dir * 2, py, 2.5, 1.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.ellipse(px + dir * 2, py, 1.2, 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // 3. Radar dome base
  const domeBaseX = fx + W * 0.48 + dir * (MODULE_SKEW * 0.4);
  const domeBaseY = collarY - 4 - MODULE_TOP_D * 0.35;

  ctx.fillStyle = '#0a0f18';
  ctx.beginPath();
  ctx.ellipse(domeBaseX, domeBaseY + 2, 13, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = frontMid;
  ctx.beginPath();
  ctx.ellipse(domeBaseX, domeBaseY, 12, 5.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = highlight;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(domeBaseX - 7, domeBaseY - 6, 14, 7);
  ctx.fillStyle = frontDark;
  ctx.fillRect(domeBaseX - 6, domeBaseY - 5, 12, 6);
  ctx.fillStyle = specLine;
  ctx.fillRect(domeBaseX - 6, domeBaseY - 5, 1.5, 6);

  // Ball gimbal
  const ballY = domeBaseY - 10;
  ctx.fillStyle = '#0a0f18';
  ctx.beginPath();
  ctx.arc(domeBaseX, ballY, 8.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = deckLight;
  ctx.beginPath();
  ctx.arc(domeBaseX, ballY, 7.5, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = specLine;
  ctx.beginPath();
  ctx.arc(domeBaseX - 2, ballY - 2, 3, 0, Math.PI * 2);
  ctx.fill();

  // Visor slot
  const visY = ballY + 0.5;
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(domeBaseX - 6, visY - 2, 12, 4);
  ctx.fillStyle = teamGlow;
  ctx.fillRect(domeBaseX - 5, visY - 1, 10, 2);

  // 4. Lattice antenna mast
  const mastBaseX = isWest ? fx + W - 14 : fx + 14;
  const mastBaseY = collarY - 4 - MODULE_TOP_D * 0.25;
  const mastH = 34;
  const mastW = 9;

  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(mastBaseX - mastW / 2 - 1, mastBaseY - 3, mastW + 2, 4);
  ctx.fillStyle = '#475569';
  ctx.fillRect(mastBaseX - mastW / 2, mastBaseY - 2, mastW, 3);
  ctx.fillStyle = specLine;
  ctx.fillRect(mastBaseX - mastW / 2, mastBaseY - 2, mastW, 0.8);

  const topMastX = mastBaseX - dir * 1.5;
  const topMastY = mastBaseY - mastH;

  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(mastBaseX - mastW / 2, mastBaseY);
  ctx.lineTo(topMastX - 2, topMastY);
  ctx.moveTo(mastBaseX + mastW / 2, mastBaseY);
  ctx.lineTo(topMastX + 2, topMastY);
  ctx.stroke();

  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(mastBaseX - mastW / 2, mastBaseY);
  ctx.lineTo(topMastX - 2, topMastY);
  ctx.moveTo(mastBaseX + mastW / 2, mastBaseY);
  ctx.lineTo(topMastX + 2, topMastY);
  ctx.stroke();

  // Diagonal rungs
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  const rungs = 4;
  for (let r = 0; r < rungs; r++) {
    const rat1 = r / rungs;
    const rat2 = (r + 1) / rungs;
    const y1 = mastBaseY - rat1 * mastH;
    const y2 = mastBaseY - rat2 * mastH;
    const xL1 = (mastBaseX - mastW / 2) + rat1 * ((topMastX - 2) - (mastBaseX - mastW / 2));
    const xR1 = (mastBaseX + mastW / 2) + rat1 * ((topMastX + 2) - (mastBaseX + mastW / 2));
    const xL2 = (mastBaseX - mastW / 2) + rat2 * ((topMastX - 2) - (mastBaseX - mastW / 2));
    const xR2 = (mastBaseX + mastW / 2) + rat2 * ((topMastX + 2) - (mastBaseX + mastW / 2));

    ctx.moveTo(xL1, y1);
    ctx.lineTo(xR2, y2);
    ctx.moveTo(xL2, y2);
    ctx.lineTo(xR2, y2);
  }
  ctx.stroke();

  // Mast top & dipoles
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(topMastX - 6, topMastY - 2, 12, 3);
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(topMastX - 5, topMastY - 1.5, 10, 2);

  const dipoles = [-4, 4];
  for (let d = 0; d < 2; d++) {
    const ox = dipoles[d];
    ctx.strokeStyle = '#0a0f18';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(topMastX + ox, topMastY - 1);
    ctx.lineTo(topMastX + ox, topMastY - 9);
    ctx.stroke();
    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(topMastX + ox, topMastY - 1);
    ctx.lineTo(topMastX + ox, topMastY - 9);
    ctx.stroke();
  }

  // Flexible hose from generator to dome
  ctx.strokeStyle = '#0a0f18';
  ctx.lineWidth = 3.5;
  ctx.beginPath();
  ctx.moveTo(genX + (isWest ? genW : 0), genY - 2);
  if (typeof ctx.bezierCurveTo === 'function') {
    ctx.bezierCurveTo(
      genX + (isWest ? genW + 8 : -8), genY + 3,
      domeBaseX - dir * 10, domeBaseY + 2,
      domeBaseX - dir * 4, domeBaseY
    );
  } else {
    ctx.lineTo(domeBaseX - dir * 4, domeBaseY);
  }
  ctx.stroke();
  ctx.strokeStyle = '#eab308';
  ctx.lineWidth = 1.8;
  ctx.stroke();

  const entry = {
    canvas,
    w: bw,
    h: bh,
    offX: -(fx + W / 2),
    offY: -fy
  };
  moduleBitmapCache.set(key, entry);
  return entry;
}

// High-intensity electrical arc and incandescent spark fountain for robotic drone assembly
function _drawWeldingFx(ctx, nx, ny, t, ticks, armIdx, baseAngle) {
  // 1. Molten Weld Seam Heat Spot on chassis joint
  ctx.fillStyle = 'rgba(249, 115, 22, 0.45)';
  ctx.beginPath();
  ctx.arc(nx, ny, 3.2, 0, Math.PI * 2);
  ctx.fill();

  // 2. Electrical Plasma Arc Bloom
  const arcPulse = Math.sin(t * 50.0 + armIdx * 17.0) * 0.8;
  // Outer cyan/electric-blue glow
  ctx.fillStyle = 'rgba(0, 240, 255, 0.50)';
  ctx.beginPath();
  ctx.arc(nx, ny, 5.0 + arcPulse, 0, Math.PI * 2);
  ctx.fill();

  // Inner blinding white core
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(nx, ny, 2.0 + arcPulse * 0.4, 0, Math.PI * 2);
  ctx.fill();

  // Momentary arc pop flare
  if ((ticks + armIdx * 5) % 7 === 0) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.beginPath();
    ctx.arc(nx, ny, 7.0, 0, Math.PI * 2);
    ctx.fill();
  }

  // 3. Incandescent Flying Sparks (10 particles per arm with gravity and thermal decay)
  for (let s = 0; s < 10; s++) {
    const sparkLife = ((t * 14.0 + s * 0.10 + armIdx * 0.37) % 1.0);
    const angleJitter = Math.sin(s * 67.3 + Math.floor(t * 10)) * 1.15;
    const angle = baseAngle + angleJitter;
    const speed = 7.0 + (s * 3.7) % 10.0;
    const dist = sparkLife * speed;
    const dropY = sparkLife * sparkLife * 11.0;

    const px = nx + Math.cos(angle) * dist;
    const py = ny + Math.sin(angle) * dist + dropY;

    // Fast velocity streak / motion blur tail pointing back toward nozzle
    const tailDist = Math.min(2.5, dist * 0.35);
    const tx = px - Math.cos(angle) * tailDist;
    const ty = py - (Math.sin(angle) * tailDist + sparkLife * 3.2);

    // Thermal cooling color progression: White-hot -> Searing yellow -> Molten amber -> Crimson
    let col, lw;
    if (sparkLife < 0.25) {
      col = '#ffffff';
      lw = 1.3;
    } else if (sparkLife < 0.55) {
      col = '#fef08a';
      lw = 1.1;
    } else if (sparkLife < 0.80) {
      col = '#f59e0b';
      lw = 0.9;
    } else {
      col = '#ea580c';
      lw = 0.7;
    }

    ctx.strokeStyle = col;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(px, py);
    ctx.stroke();

    // White-hot spark head glint for youngest particles
    if (sparkLife < 0.45) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px - 0.5, py - 0.5, 1, 1);
    }
  }
}

export const modulesMixin = {
  // Draws both teams' module towers. Called from render() right after the
  // fortresses, so a module resting on the roof correctly overlaps it.
  renderModuleTowers(ctx, battlefield) {
    const ms = battlefield.moduleSystem;
    if (!ms) return;
    this._renderModuleTower(ctx, battlefield, ms, 'western', '#00f0ff');
    this._renderModuleTower(ctx, battlefield, ms, 'eastern', '#ff0055');
  },

  _renderModuleTower(ctx, battlefield, ms, team, mainColor) {
    const stack = ms.stack[team];
    const pending = ms.pending[team];
    const retracting = ms.fx[team]?.retractingRigs;
    const hasRetracting = retracting && retracting.length > 0;
    if (stack.length === 0 && pending.length === 0 && !hasRetracting) return;

    const base = battlefield.bases[team];
    ctx.save();
    const BASE_SCALE = 0.864; // +8% proportional hochskaliert (0.8 * 1.08)
    ctx.translate(base.x, base.y);
    ctx.scale(BASE_SCALE, BASE_SCALE);
    ctx.translate(-base.x, -base.y);

    const isLow = this.quality === 'low';
    const t = performance.now() * 0.001;
    const slotX = ms.getSlotX(team);
    const dir = team === 'western' ? 1 : -1;

    // Building scale 1.06 around (bAnchorX, bAnchorY) matching bunker foundation
    const DOCK_BLOCK_W = 88;
    const groundY = base.y + 26;
    const dockX = dir === 1 ? base.x - 40 : base.x + 40 - DOCK_BLOCK_W;
    const bAnchorX = dockX + DOCK_BLOCK_W / 2;
    const bAnchorY = groundY;
    const BLDG_SCALE = 1.06;

    ctx.save();
    ctx.translate(bAnchorX, bAnchorY);
    ctx.scale(BLDG_SCALE, BLDG_SCALE);
    ctx.translate(-bAnchorX, -bAnchorY);

    // 1. Landed modules, bottom-up
    for (let i = 0; i < stack.length; i++) {
      const entry = stack[i];
      const y = ms.getSlotY(team, entry.slot) + entry.slot * MODULE_TOP_D;
      this._drawModuleBody(ctx, entry.moduleId, team, slotX, y, 1);
      this._drawModuleOverlay(ctx, ms, team, entry, slotX, y, mainColor, dir, t, isLow);
    }

    // 2. Cap module (Industrial Apex Roof Superstructure) once stack is full
    if (ms.hasCap(team)) {
      const topSlot = stack.length - 1;
      const topSlotY = topSlot >= 0 ? (ms.getSlotY(team, topSlot) + topSlot * MODULE_TOP_D) : ms.getSlotY(team, 0);
      const topFrontY = topSlotY - 9;
      const cap = buildCapBitmap(team);
      ctx.drawImage(cap.canvas, slotX + cap.offX, topFrontY + cap.offY);
      this._drawCapOverlay(ctx, slotX, topFrontY, team, mainColor, dir, t, isLow);
    }

    // 3. Modules still descending on their cable. Multi-phase descent & elastic touchdown settling
    for (let i = 0; i < pending.length; i++) {
      const entry = pending[i];
      const targetY = ms.getSlotY(team, entry.slot) + entry.slot * MODULE_TOP_D;
      const p = ms.deployProgress(entry);

      let modY = targetY;
      if (p < 0.75) {
        const normP = p / 0.75;
        const ease = 1 - Math.pow(1 - normP, 2.4);
        modY = targetY - (1 - ease) * 190;
      } else {
        const normP = (p - 0.75) / 0.25;
        const bounce = Math.sin(normP * Math.PI * 2) * Math.exp(-normP * 4) * 3.5;
        modY = targetY - bounce;
      }

      this._drawModuleBody(ctx, entry.moduleId, team, slotX, modY, 1);
      this._drawModuleOverlay(ctx, ms, team, entry, slotX, modY, mainColor, dir, t, isLow);
      this._drawCraneCableRig(ctx, slotX, modY, targetY, dir, p, false, team, isLow);
    }

    // 4. Retracting cable rigs after touchdown (hoist bar ascends into the sky with swaying cables)
    if (hasRetracting) {
      for (let i = 0; i < retracting.length; i++) {
        const rig = retracting[i];
        const targetY = ms.getSlotY(team, rig.slot) + rig.slot * MODULE_TOP_D;
        const rigP = 1 - Math.max(0, rig.ticksRemaining) / rig.ticksTotal;
        this._drawCraneCableRig(ctx, slotX, targetY, targetY, dir, rigP, true, team, isLow);
      }
    }

    ctx.restore(); // BLDG_SCALE restore
    ctx.restore(); // BASE_SCALE restore
  },

  // One drawImage per module - the whole point of the bake (see file header).
  _drawModuleBody(ctx, moduleId, team, x, y, alpha) {
    const bmp = buildModuleBitmap(moduleId, team);
    if (alpha < 1) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.drawImage(bmp.canvas, x + bmp.offX, y + bmp.offY);
      ctx.restore();
    } else {
      ctx.drawImage(bmp.canvas, x + bmp.offX, y + bmp.offY);
    }
  },

  // Live dynamic overlays on top of the baked cube. Quality-gated (INVARIANTS #4).
  _drawModuleOverlay(ctx, ms, team, entry, x, y, mainColor, dir, t, isLow) {
    const isWest = dir === 1;
    const def = MODULE_DEFS[entry.moduleId];
    const fx = x - MODULE_W / 2;
    const fy = y - 9;
    const fh = MODULE_FRONT_H;

    // Team accent light strip along bottom chord - always on for quick reading
    ctx.fillStyle = mainColor;
    ctx.globalAlpha = 0.75;
    ctx.fillRect(fx + 10, fy + fh - 3.5, MODULE_W - 20, 1.5);
    ctx.globalAlpha = 1.0;

    // Status LEDs on left column
    ctx.fillStyle = mainColor;
    ctx.fillRect(fx + 1.5, fy + 10, 1.8, 1.8);
    ctx.fillRect(fx + 1.5, fy + 15, 1.8, 1.8);
    ctx.fillRect(fx + 1.5, fy + 20, 1.8, 1.8);

    if (def?.interior === 'centrifuge_on_girders') {
      const cx = fx + (isWest ? MODULE_W * 0.44 : MODULE_W * 0.56) + dir * (MODULE_SKEW * 0.4);
      const drumY = fy + fh * 0.44;

      // 1. Horizontal Rotating Impeller (blades projected in 3D horizontal ellipse)
      const topY = drumY - 3.0;
      const cylH = 10.0;
      const rx = 20.0;
      const ry = 8.5;
      const rimRx = rx - 4.5;
      const rimRy = ry - 2.2;
      const spin = t * 6.0;
      const bladeRx = rimRx - 2.2;
      const bladeRy = rimRy - 1.2;

      if (isLow) {
        // Fast zero-allocation path for low tier: rotating cross blades without array/object allocations
        for (let i = 0; i < 4; i++) {
          const a = spin + (i * Math.PI) / 2;
          const tipX = cx + Math.cos(a) * bladeRx;
          const tipY = topY + Math.sin(a) * bladeRy;
          ctx.strokeStyle = Math.sin(a) > 0 ? '#cbd5e1' : '#64748b';
          ctx.lineWidth = 2.0;
          ctx.beginPath();
          ctx.moveTo(cx, topY);
          ctx.lineTo(tipX, tipY);
          ctx.stroke();
        }
      } else {
        for (let i = 0; i < 4; i++) {
          const a = spin + (i * Math.PI) / 2;
          _bladesScratch[i].angle = a;
          _bladesScratch[i].sinA = Math.sin(a);
          _bladesScratch[i].cosA = Math.cos(a);
        }
        _bladesScratch.sort((b1, b2) => b1.sinA - b2.sinA);

        for (let i = 0; i < _bladesScratch.length; i++) {
          const b = _bladesScratch[i];
          const tipX = cx + b.cosA * bladeRx;
          const tipY = topY + b.sinA * bladeRy;
          const normAngle = b.angle + Math.PI / 2;
          const bladeW = 2.4;
          const perpX = Math.cos(normAngle) * (bladeW * 0.5);
          const perpY = Math.sin(normAngle) * (bladeW * 0.5 * (ry / rx));

          ctx.fillStyle = b.sinA > 0 ? '#cbd5e1' : '#64748b';
          ctx.beginPath();
          ctx.moveTo(cx, topY);
          ctx.lineTo(tipX - perpX, tipY - perpY);
          ctx.lineTo(tipX + perpX, tipY + perpY);
          ctx.closePath();
          ctx.fill();

          ctx.strokeStyle = b.sinA > 0 ? '#ffffff' : '#94a3b8';
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(cx, topY);
          ctx.lineTo(tipX + perpX, tipY + perpY);
          ctx.stroke();
        }
      }

      // Central glowing rotor hub & plasma core
      ctx.fillStyle = '#0a0f18';
      ctx.beginPath();
      ctx.ellipse(cx, topY, 4.8, 2.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.ellipse(cx, topY, 3.8, 1.8, 0, 0, Math.PI * 2);
      ctx.fill();

      const plasmaGlow = team === 'western' ? '#00f0ff' : '#ff0055';
      ctx.fillStyle = plasmaGlow;
      ctx.beginPath();
      ctx.ellipse(cx, topY, 2.6, 1.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(cx, topY, 1.2, 0.6, 0, 0, Math.PI * 2);
      ctx.fill();

      // Horizontal plasma orbit ring (only on medium/high to conserve fillrate)
      if (!isLow) {
        ctx.strokeStyle = plasmaGlow;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        const orbitRx = 7.0 + Math.sin(spin * 3) * 1.0;
        const orbitRy = orbitRx * (ry / rx);
        ctx.ellipse(cx, topY, orbitRx, orbitRy, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      // 2. Angled accelerator tube with 3 coil stripes indicating loaded marines (0 to 3)
      const tubeStartX = cx + dir * 14.5;
      const tubeStartY = topY + cylH * 0.52;
      const tubeAngle = -0.50;

      ctx.save();
      ctx.translate(tubeStartX, tubeStartY);
      ctx.scale(dir, 1);
      ctx.rotate(tubeAngle);

      const state = ms.state[team]?.[entry.moduleId];
      const loaded = state?.loaded || 0;
      const fxState = ms.fx[team]?.[entry.moduleId];

      for (let cIdx = 0; cIdx < 3; cIdx++) {
        const rx = COIL_OFFSETS[cIdx];
        const isLoaded = cIdx < loaded;

        if (isLoaded) {
          if (isLow) {
            // Fast crisp solid fill on low settings - zero trigonometric overhead, 100% readable status
            ctx.fillStyle = team === 'western' ? '#00f0ff' : '#ff0055';
            ctx.fillRect(rx, -5.8 - 2, 2.5, 11.6 + 4);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(rx + 0.5, -4, 1.5, 8);
          } else {
            // Active charged state: breathing plasma wave + flash pulse on capture
            const pulse = fxState ? (fxState[`pulse${cIdx}`] || 0) : 0;
            const breath = Math.sin(t * 4.0 + cIdx * 1.2) * 0.15 + 0.85;
            const flash = pulse > 0 ? (pulse / 0.35) : 0;

            let glowCol;
            if (team === 'western') {
              glowCol = flash > 0.4 ? '#ffffff' : (breath > 0.85 ? '#e0f7fa' : '#00f0ff');
            } else {
              glowCol = flash > 0.4 ? '#ffffff' : (breath > 0.85 ? '#ffe4e6' : '#ff0055');
            }

            ctx.fillStyle = glowCol;
            ctx.fillRect(rx, -5.8 - 2, 2.5, 11.6 + 4);

            // Core bright center streak
            ctx.fillStyle = flash > 0.1 ? '#ffffff' : (team === 'western' ? '#e0f7fa' : '#ffe4e6');
            ctx.fillRect(rx + 0.4, -4, 1.6, 8);

            // Full charge super-flare on 3/3 capacity
            if (loaded >= 3) {
              const superPulse = Math.sin(t * 8.0 - cIdx * 1.5) * 0.5 + 0.5;
              if (superPulse > 0.6) {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                ctx.fillRect(rx - 0.4, -5.8 - 2.5, 3.3, 11.6 + 5);
              }
            }
          }
        } else {
          // Uncharged standby slot: dark recessed housing with subtle border
          ctx.fillStyle = '#0f1724';
          ctx.fillRect(rx, -5.8 - 2, 2.5, 11.6 + 4);
          ctx.fillStyle = 'rgba(30, 41, 59, 0.6)';
          ctx.fillRect(rx + 0.4, -4, 1.7, 8);
        }
      }
      ctx.restore();
    } else if (def?.interior === 'assembly_bay') {
      const cx = fx + (isWest ? MODULE_W * 0.46 : MODULE_W * 0.54) + dir * (MODULE_SKEW * 0.35);
      const cy = fy + fh * 0.42;
      const state = ms.state[team]?.[entry.moduleId];
      const ticks = state?.ticks || 0;
      const ready = !!state?.ready;
      const fxState = ms.fx[team]?.[entry.moduleId];
      const launchFlash = fxState?.launchFlash || 0;

      // Tilting cradle mount position
      const bedY = cy + 0.5;
      const droneCenterY = bedY - 4.5;

      // Departure / Liftoff state (smooth takeoff out of the assembly bay)
      const departTimer = fxState?.departTimer || 0;
      const departTotal = fxState?.departTotal || 0.33;
      const tsDeparting = fxState?.departUntil && (t < fxState.departUntil);
      const isDeparting = (departTimer > 0) || tsDeparting;
      let departP = 0;
      if (departTimer > 0) {
        departP = Math.min(1.0, Math.max(0, 1.0 - departTimer / departTotal));
      } else if (tsDeparting) {
        departP = Math.min(1.0, Math.max(0, 1.0 - (fxState.departUntil - t) / 0.33));
      }

      // 4-Phase Organic Assembly:
      // Phase 1 (0 - 240 ticks, 0-4s):
      //   - 0 to 45 ticks (0 - 0.75s): Empty launch cradle. Robotic arms move in and strike initial welding arcs at the bare bed mounts.
      //   - 45 to 240 ticks (0.75 - 4s): X-frame cross appears and is actively welded onto the cradle.
      // Phase 2 (240 - 420 ticks, 4-7s): Fitting explosive payload & avionics
      // Phase 3 (420 - 540 ticks, 7-9s): Rotor spool-up & spin test
      // Phase 4 (540 - 600 ticks or ready, 9-10s+): Armed & awaiting launch
      // While departing, old drone is visibly lifting off out of the bay; cradle becomes empty only AFTER departure.

      const isCradleEmpty = !isDeparting && (ticks < 45 && !ready);
      const isPhase1 = !isDeparting && (ticks < 240 && !ready);
      const isPhase2 = !isDeparting && (ticks >= 240 && ticks < 420 && !ready);
      const isPhase3 = !isDeparting && (ticks >= 420 && ticks < 540 && !ready);
      const isPhase4 = isDeparting || ticks >= 540 || ready;

      // 1. Robotic Tooltips / Welding Arms articulated movement
      // When cradle is empty (first 45 ticks), arm tips target the bare bed mounts
      const armTargetY = isCradleEmpty ? (bedY - 1.5) : (bedY - 4);
      const arm1End = { x: cx - 4, y: armTargetY };
      const arm2End = { x: cx + 4, y: armTargetY };

      if (isPhase1) {
        // Active welding motion: tips jittering across the frame joints
        const jitter = Math.sin(t * 18) * 1.5;
        arm1End.x += jitter;
        arm1End.y += Math.cos(t * 14) * 0.8;
        arm2End.x -= jitter;
        arm2End.y += Math.sin(t * 16) * 0.8;

        // Forearm links
        ctx.strokeStyle = '#64748b';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(cx - 8, cy - 8);
        ctx.lineTo(arm1End.x, arm1End.y);
        ctx.moveTo(cx + 8, cy - 8);
        ctx.lineTo(arm2End.x, arm2End.y);
        ctx.stroke();

        // Welding nozzles
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(arm1End.x - 0.75, arm1End.y - 0.75, 1.5, 1.5);
        ctx.fillRect(arm2End.x - 0.75, arm2End.y - 0.75, 1.5, 1.5);

        // Dynamic Welding Arc & Incandescent Spark Shower (Rich particle fountain)
        const arc1Active = (ticks % 18 < 13);
        const arc2Active = ((ticks + 8) % 20 < 14);

        if (!isLow) {
          if (arc1Active) {
            _drawWeldingFx(ctx, arm1End.x, arm1End.y, t, ticks, 0, Math.PI * 0.95);
          }
          if (arc2Active) {
            _drawWeldingFx(ctx, arm2End.x, arm2End.y, t, ticks, 1, 0.15);
          }
        } else if (arc1Active || arc2Active) {
          // Lightweight fast-path for low-spec devices (zero trig overhead)
          const wx = arc1Active ? arm1End.x : arm2End.x;
          const wy = arc1Active ? arm1End.y : arm2End.y;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(wx - 1.5, wy - 1.5, 3, 3);
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(wx - 3, wy - 2, 1, 1);
          ctx.fillRect(wx + 3, wy - 2, 1, 1);
          ctx.fillRect(wx - 2, wy + 3, 1, 1);
          ctx.fillRect(wx + 2, wy + 3, 1, 1);
        }
      } else {
        // Arms parked / retracted upwards
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(cx - 8, cy - 8);
        ctx.lineTo(cx - 10, cy - 14);
        ctx.moveTo(cx + 8, cy - 8);
        ctx.lineTo(cx + 10, cy - 14);
        ctx.stroke();
      }

      // 2. Drone Assembly on Cradle (or visibly lifting off during departure)
      if (!isCradleEmpty) {
        let drawCenterX = cx;
        let drawCenterY = droneCenterY;
        let tiltAng = 0;

        if (isDeparting) {
          // Smooth liftoff acceleration curve out of the bay aperture
          const easeLift = Math.pow(departP, 1.6);
          drawCenterX = cx + dir * (easeLift * 28);
          drawCenterY = droneCenterY - (Math.sin(departP * Math.PI * 0.75) * 14 + easeLift * 10);
          tiltAng = dir * (0.22 + easeLift * 0.32);
        } else {
          // Tilt pitch during Phase 4 (tilt forward toward arena)
          tiltAng = isPhase4 ? dir * 0.22 : 0;
          // High-RPM motor vibration when spooled up / waiting on pad
          const vibX = isPhase4 ? Math.sin(t * 60.0) * 0.35 : 0;
          const vibY = isPhase4 ? Math.cos(t * 75.0) * 0.22 : 0;
          drawCenterX += vibX;
          drawCenterY += vibY;
        }

        ctx.save();
        ctx.translate(drawCenterX, drawCenterY);
        ctx.rotate(tiltAng);

        // (A) The X-Frame (present from tick 45+)
        // When first appearing (ticks 45-85), seams glow with molten welding heat
        const isMolten = isPhase1 && ticks < 85;
        const frameColor = isMolten ? '#ea580c' : '#1e293b';
        const frameHighlight = isMolten ? '#fef08a' : '#475569';

        ctx.strokeStyle = frameColor;
        ctx.lineWidth = 2.0;
        ctx.beginPath();
        ctx.moveTo(-6, -2);
        ctx.lineTo(6, 2);
        ctx.moveTo(-6, 2);
        ctx.lineTo(6, -2);
        ctx.stroke();

        ctx.strokeStyle = frameHighlight;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(-6, -2);
        ctx.lineTo(6, 2);
        ctx.moveTo(-6, 2);
        ctx.lineTo(6, -2);
        ctx.stroke();

        // (B) Central chassis & Explosive block (fitted in Phase 2+)
        if (isPhase2 || isPhase3 || isPhase4) {
          // C4 Explosive Pack (Olive/Yellowish block)
          ctx.fillStyle = '#0a0f18';
          ctx.fillRect(-3, -2.5, 6, 5);
          ctx.fillStyle = '#ca8a04';
          ctx.fillRect(-2.5, -2, 5, 4);
          // Red detonator wire / tape
          ctx.fillStyle = '#ef4444';
          ctx.fillRect(-1, -2, 2, 4);
          ctx.fillStyle = '#fde047';
          ctx.fillRect(1, -1.5, 1, 1);
        } else if (ticks >= 70) {
          // Bare hub (materializes after initial cross welding)
          ctx.fillStyle = '#0a0f18';
          ctx.fillRect(-2, -1.5, 4, 3);
          ctx.fillStyle = isMolten ? '#f97316' : '#334155';
          ctx.fillRect(-1.5, -1, 3, 2);
        }

        // (C) Propellers / Rotors (Spool-up in Phase 3, Dynamic High-Speed Spin in Phase 4)
        if (isPhase2 || isPhase3 || isPhase4) {
          const rotorPositions = [
            { x: -6, y: -2 },
            { x: 6, y: -2 },
            { x: -6, y: 2 },
            { x: 6, y: 2 }
          ];

          for (let r = 0; r < 4; r++) {
            const rp = rotorPositions[r];
            // Rotor motor cap
            ctx.fillStyle = '#0a0f18';
            ctx.fillRect(rp.x - 1, rp.y - 1, 2, 2);
            ctx.fillStyle = '#94a3b8';
            ctx.fillRect(rp.x - 0.5, rp.y - 0.5, 1, 1);

            if (isPhase3) {
              // Accelerating rotor blades
              const spinSpeed = ((ticks - 420) / 120) * 25.0;
              const rAng = t * spinSpeed + r * 1.57;
              ctx.strokeStyle = '#cbd5e1';
              ctx.lineWidth = 0.8;
              ctx.beginPath();
              ctx.moveTo(rp.x - Math.cos(rAng) * 3.5, rp.y - Math.sin(rAng) * 1.5);
              ctx.lineTo(rp.x + Math.cos(rAng) * 3.5, rp.y + Math.sin(rAng) * 1.5);
              ctx.stroke();
            } else if (isPhase4) {
              // Full throttle: high-speed spinning blades & dynamic air shimmer (never frozen)
              const spin = t * 48.0 + r * 1.57;
              const cosS = Math.cos(spin);
              const sinS = Math.sin(spin);

              // Shimmering blurred disc
              const discAlpha = isLow ? 0.35 : (0.28 + Math.sin(t * 22.0 + r * 1.8) * 0.12);
              ctx.fillStyle = `rgba(226, 232, 240, ${discAlpha})`;
              ctx.beginPath();
              ctx.ellipse(rp.x, rp.y, 4.2, 1.8, 0, 0, Math.PI * 2);
              ctx.fill();

              // Rapidly rotating primary blade streak
              ctx.strokeStyle = '#f8fafc';
              ctx.lineWidth = 1.0;
              ctx.beginPath();
              ctx.moveTo(rp.x - cosS * 4.0, rp.y - sinS * 1.6);
              ctx.lineTo(rp.x + cosS * 4.0, rp.y + sinS * 1.6);
              ctx.stroke();

              // Perpendicular secondary blade streak
              const cosS2 = Math.cos(spin + 1.57);
              const sinS2 = Math.sin(spin + 1.57);
              ctx.strokeStyle = 'rgba(203, 213, 225, 0.6)';
              ctx.lineWidth = 0.8;
              ctx.beginPath();
              ctx.moveTo(rp.x - cosS2 * 3.6, rp.y - sinS2 * 1.4);
              ctx.lineTo(rp.x + cosS2 * 3.6, rp.y + sinS2 * 1.4);
              ctx.stroke();

              // Blade tip glint
              if (!isLow) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(rp.x + cosS * 3.8 - 0.5, rp.y + sinS * 1.5 - 0.5, 1, 1);
              }
            } else {
              // Static parked blades in Phase 2
              ctx.strokeStyle = '#475569';
              ctx.lineWidth = 0.8;
              ctx.beginPath();
              ctx.moveTo(rp.x - 3, rp.y);
              ctx.lineTo(rp.x + 3, rp.y);
              ctx.stroke();
            }
          }
        }

        // (D) Rear Status / Arming LED (Phase 4 sharp tactical military double-strobe)
        if (isPhase4) {
          const strobe = (t * 4.0) % 1.0;
          const isFlash = strobe < 0.15 || (strobe > 0.25 && strobe < 0.40);
          ctx.fillStyle = isFlash ? '#ffffff' : mainColor;
          ctx.globalAlpha = isFlash ? 1.0 : 0.4;
          ctx.fillRect(-dir * 2.5 - 0.8, -0.8, 1.6, 1.6);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(-dir * 2.5 - 0.4, -0.4, 0.8, 0.8);
          ctx.globalAlpha = 1.0;
        }

        // (E) Dynamic afterburner thrusters during departure liftoff
        if (isDeparting) {
          const rotorPositions = [
            { x: -6, y: -2 },
            { x: 6, y: -2 },
            { x: -6, y: 2 },
            { x: 6, y: 2 }
          ];
          const thrustLen = 5.0 + Math.sin(t * 45.0) * 2.0;
          ctx.save();
          for (let r = 0; r < 4; r++) {
            const rp = rotorPositions[r];
            ctx.fillStyle = mainColor;
            ctx.globalAlpha = 0.85;
            ctx.beginPath();
            ctx.moveTo(rp.x - 1.2, rp.y + 1);
            ctx.lineTo(rp.x + 1.2, rp.y + 1);
            ctx.lineTo(rp.x - dir * 1.8, rp.y + thrustLen + 2);
            ctx.fill();

            ctx.fillStyle = '#ffffff';
            ctx.globalAlpha = 1.0;
            ctx.beginPath();
            ctx.moveTo(rp.x - 0.6, rp.y + 1);
            ctx.lineTo(rp.x + 0.6, rp.y + 1);
            ctx.lineTo(rp.x - dir * 1.0, rp.y + thrustLen * 0.6 + 1);
            ctx.fill();
          }
          ctx.restore();
        }

        // (F) Tactical targeting / search beam when ready on launch pad (Option A Standby)
        if (ready && !isDeparting) {
          const sweepSin = Math.sin(t * 3.5);
          const beamLen = 32 + Math.sin(t * 5.0) * 6;
          const beamEndX = dir * (6 + beamLen);
          const beamEndY = sweepSin * 5;

          ctx.save();
          // Subtle scanner cone
          ctx.fillStyle = mainColor;
          ctx.globalAlpha = 0.08 + Math.abs(sweepSin) * 0.06;
          ctx.beginPath();
          ctx.moveTo(dir * 5, 0);
          ctx.lineTo(beamEndX, beamEndY - 3.5);
          ctx.lineTo(beamEndX, beamEndY + 3.5);
          ctx.closePath();
          ctx.fill();

          // Focused center beam laser
          ctx.strokeStyle = mainColor;
          ctx.lineWidth = 1.0;
          ctx.globalAlpha = 0.35 + Math.abs(sweepSin) * 0.25;
          ctx.beginPath();
          ctx.moveTo(dir * 5, 0);
          ctx.lineTo(beamEndX, beamEndY);
          ctx.stroke();

          // Scanner target ping dot
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = 0.7;
          ctx.fillRect(beamEndX - 0.75, beamEndY - 0.75, 1.5, 1.5);
          ctx.restore();
        }

        ctx.restore();
      }

      // Dynamic detail in right area: pulsing telemetry LED on relay box
      const rightBoxX = fx + MODULE_W - 13.0;
      const rightBoxY = fy + 14;
      const teleFlash = (t * 3.5) % 1.0 > 0.45;
      ctx.fillStyle = teleFlash ? '#f59e0b' : '#78350f';
      ctx.fillRect(rightBoxX + 3.0, rightBoxY + 3.0, 1.3, 1.3);

      // Launch flash explosion at cradle mouth when a drone just departed
      if (launchFlash > 0) {
        ctx.save();
        const flashAlpha = Math.min(1.0, launchFlash / 0.35);
        ctx.fillStyle = mainColor;
        ctx.globalAlpha = flashAlpha * 0.85;
        ctx.beginPath();
        ctx.ellipse(cx + dir * 10, droneCenterY - 2, 10, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = flashAlpha;
        ctx.beginPath();
        ctx.ellipse(cx + dir * 10, droneCenterY - 2, 5, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // 3. Front Framework Overlay:
      // Redraw the front mid tie beam and mid gusset plates so the drone, cradle,
      // and interior machinery sit physically BEHIND the steel frame beam.
      drawIBeam(ctx, fx, fy + fh * 0.52, MODULE_W, 2.6, false, true);

      const gSize = 8;
      const midGussets = [
        fx, fy + fh * 0.52 - gSize / 2,
        fx + MODULE_W - gSize, fy + fh * 0.52 - gSize / 2
      ];
      for (let c = 0; c < midGussets.length; c += 2) {
        const gx = midGussets[c];
        const gy = midGussets[c + 1];
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(gx, gy, gSize, gSize);
        ctx.fillStyle = STEEL_LIGHT;
        ctx.fillRect(gx + 0.6, gy + 0.6, gSize - 1.2, gSize - 1.2);
        ctx.fillStyle = RUST_MID;
        ctx.fillRect(gx + 1, gy + 1, 2, 2);
        ctx.fillRect(gx + gSize - 3, gy + gSize - 3, 2, 2);
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(gx + 1.2, gy + 1.2, 1.2, 1.2);
        ctx.fillRect(gx + gSize - 2.5, gy + gSize - 2.5, 1.2, 1.2);
      }
      // Re-apply left column status LED at mid height
      ctx.fillStyle = mainColor;
      ctx.fillRect(fx + 1.5, fy + 20, 1.8, 1.8);
    } else if (def?.interior === 'debating_chamber') {
      const shiftX = dir * 6.2;
      const cx = fx + MODULE_W / 2 + shiftX;
      const state = ms.state[team]?.congress_module;
      const buffIndex = state?.buffIndex || 0;
      const currentBuff = CONGRESS_BUFFS[buffIndex] || CONGRESS_BUFFS[0];
      const decreeColor = currentBuff.color || '#00ff88';
      const fxState = ms.fx[team]?.congress_module;
      const decreePulse = fxState?.decreePulse || 0;

      // 1. Holographic Globe pinnacle atop the US Capitol Dome lantern spire
      // Height budget: Top of globe at fy + 1.9 (strictly below fy) to prevent any clipping into upper modules!
      const emitterTipY = fy + 9.6;
      const holoY = fy + 5.5;
      const holoRadius = 3.6;
      const spinAngle = t * 2.5;

      ctx.save();

      // Energy linkage beam between cupola emitter collar and floating globe
      ctx.strokeStyle = decreeColor;
      ctx.lineWidth = 0.9;
      ctx.globalAlpha = 0.5 + Math.sin(t * 10) * 0.2 + decreePulse * 0.3;
      ctx.beginPath();
      ctx.moveTo(cx, emitterTipY);
      ctx.lineTo(cx, holoY + holoRadius);
      ctx.stroke();

      // Decree Proclamation Pulse wave rippling down the dome ribs
      if (decreePulse > 0) {
        ctx.save();
        const pY = (fy + 10) + (1.0 - decreePulse) * 28;
        const pW = 5 + (1.0 - decreePulse) * 24;
        ctx.strokeStyle = decreeColor;
        ctx.globalAlpha = decreePulse * 0.8;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.ellipse(cx, pY, pW / 2, 2.4, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Subtle dynamic ambient lighting on dome top
      ctx.fillStyle = decreeColor;
      ctx.globalAlpha = 0.12 + decreePulse * 0.2;
      ctx.beginPath();
      ctx.ellipse(cx, fy + 15.4, 9.0, 3.0, 0, 0, Math.PI * 2);
      ctx.fill();

      // Rotating Hologram Sphere Outer Ring
      ctx.globalAlpha = isLow ? 0.6 : (0.75 + Math.sin(t * 8) * 0.15 + decreePulse * 0.25);
      ctx.strokeStyle = decreeColor;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(cx, holoY, holoRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Rotating inner axis lines / globe longitude rings
      ctx.beginPath();
      const ringW = Math.cos(spinAngle) * holoRadius;
      ctx.ellipse(cx, holoY, Math.max(0.6, Math.abs(ringW)), holoRadius, 0, 0, Math.PI * 2);
      ctx.stroke();

      // Horizontal equator ring
      ctx.beginPath();
      ctx.moveTo(cx - holoRadius * 0.7, holoY);
      ctx.lineTo(cx + holoRadius * 0.7, holoY);
      ctx.stroke();

      // Central glowing particle / spark
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(cx - 0.7, holoY - 0.7, 1.4, 1.4);

      ctx.restore();

      // 2. Front Framework Overlay (Variante B):
      // Redraw front mid tie beam and gusset plates so the rotunda drum sits physically BEHIND
      // the mid tie beam, while the Capitol dome and lantern rise behind and above it.
      drawIBeam(ctx, fx, fy + fh * 0.52, MODULE_W, 2.6, false, true);

      const gSize = 8;
      const midGussets = [
        fx, fy + fh * 0.52 - gSize / 2,
        fx + MODULE_W - gSize, fy + fh * 0.52 - gSize / 2
      ];
      for (let c = 0; c < midGussets.length; c += 2) {
        const gx = midGussets[c];
        const gy = midGussets[c + 1];
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(gx, gy, gSize, gSize);
        ctx.fillStyle = STEEL_LIGHT;
        ctx.fillRect(gx + 0.6, gy + 0.6, gSize - 1.2, gSize - 1.2);
        ctx.fillStyle = RUST_MID;
        ctx.fillRect(gx + 1, gy + 1, 2, 2);
        ctx.fillRect(gx + gSize - 3, gy + gSize - 3, 2, 2);
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(gx + 1.2, gy + 1.2, 1.2, 1.2);
        ctx.fillRect(gx + gSize - 2.5, gy + gSize - 2.5, 1.2, 1.2);
      }
      ctx.fillStyle = decreeColor;
      ctx.fillRect(fx + 1.5, fy + 20, 1.8, 1.8);
    }
  },

  // Dynamic overlay on the apex roof superstructure:
  // Radar sweep line, rotating parabolic dish, and pulsing red aviation beacon.
  _drawCapOverlay(ctx, slotX, topFrontY, team, mainColor, dir, t, isLow) {
    const isWest = dir === 1;
    const fx = slotX - MODULE_W / 2;
    const fy = topFrontY;
    const collarH = 7;
    const collarY = fy - collarH;

    const domeBaseX = fx + MODULE_W * 0.48 + dir * (MODULE_SKEW * 0.4);
    const domeBaseY = collarY - 4 - MODULE_TOP_D * 0.35;
    const ballY = domeBaseY - 10;
    const visY = ballY + 0.5;

    // 1. Radar dome scan line (optics visor)
    if (!isLow) {
      const sweepX = domeBaseX - 4 + ((Math.sin(t * 4) * 0.5 + 0.5) * 8);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(sweepX, visY - 1, 2, 2);
    }

    // 2. Rotating parabolic radar dish
    const dishAngle = isLow ? (dir === 1 ? 0.3 : -0.3) : (Math.sin(t * 1.8) * 0.45 + (dir === 1 ? 0.3 : -0.3));
    const dishMountX = domeBaseX;
    const dishMountY = ballY - 8;

    ctx.save();
    ctx.translate(dishMountX, dishMountY);
    ctx.rotate(dishAngle);

    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(-2, -4, 4, 5);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(-1, -3, 2, 4);

    ctx.strokeStyle = '#0a0f18';
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.arc(0, -9, 10, Math.PI * 0.75, Math.PI * 1.25);
    ctx.stroke();

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.arc(0, -9, 10, Math.PI * 0.75, Math.PI * 1.25);
    ctx.stroke();

    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.arc(0, -9, 10, Math.PI * 0.75, Math.PI * 1.25);
    ctx.stroke();

    // Feed horn
    ctx.strokeStyle = '#0a0f18';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(-7, -9);
    ctx.stroke();
    ctx.fillStyle = mainColor;
    ctx.fillRect(-8.5, -10.5, 3, 3);

    ctx.restore();

    // 3. Obstacle beacon on top of antenna mast
    const mastBaseX = isWest ? fx + MODULE_W - 14 : fx + 14;
    const mastBaseY = collarY - 4 - MODULE_TOP_D * 0.25;
    const mastH = 34;
    const topMastX = mastBaseX - dir * 1.5;
    const topMastY = mastBaseY - mastH;
    const beaconY = topMastY - 4;

    const pulseCycle = (t * 2.8) % (Math.PI * 2);
    const pulseVal = Math.pow(Math.max(0, Math.sin(pulseCycle)), 3.5);
    const rVal = Math.round(130 + pulseVal * 125);

    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.arc(topMastX, beaconY, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = `rgb(${rVal}, 12, 28)`;
    ctx.beginPath();
    ctx.arc(topMastX, beaconY, 2.2, 0, Math.PI * 2);
    ctx.fill();

    if (!isLow) {
      if (pulseVal > 0.4) {
        ctx.fillStyle = ALPHA_LUT_WHITE[Math.round(pulseVal * 0.75 * 16)];
        ctx.beginPath();
        ctx.arc(topMastX, beaconY, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      if (pulseVal > 0.05) {
        ctx.fillStyle = ALPHA_LUT_RED_BEACON[Math.round(pulseVal * 0.42 * 16)];
        ctx.beginPath();
        ctx.arc(topMastX, beaconY, 4 + pulseVal * 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  },

  // Dynamic Crane Harness & Cable Retraction System (Industrial Spreader with 4-leg sling bridles)
  _drawCraneCableRig(ctx, slotX, modY, targetY, dir, progress, isRetracting, team, isLow) {
    const W = MODULE_W;
    const topDepth = MODULE_TOP_D;
    const skewX = MODULE_SKEW;
    const baseExtraSkew = 2.5;
    const shadowSkewX = skewX + baseExtraSkew;
    const T = 4.2;
    const isWest = dir === 1;

    const fx = slotX - W / 2;
    const fy = modY - 9;
    const by = fy - topDepth;
    const targetFy = targetY - 9;
    const targetRoofCenterY = targetFy - topDepth * 0.5;

    let hookX = slotX + dir * (skewX * 0.45);
    let hookY;
    let sag = 0;
    let isAttached = true;
    let slingDangle = 0;

    if (!isRetracting) {
      if (progress < 0.75) {
        const p = progress / 0.75;
        const curRoofCenterY = fy - topDepth * 0.5;
        hookY = curRoofCenterY - 46;
        hookX += Math.sin(p * Math.PI * 4) * (1 - p) * 1.8;
      } else {
        const p = (progress - 0.75) / 0.25;
        const slackDip = Math.sin(p * Math.PI) * 6;
        hookY = targetRoofCenterY - 46 + slackDip;
        sag = Math.sin(p * Math.PI) * 7.5;
        isAttached = p < 0.7; // Uncouples at 70% of touchdown phase

        // In-frame dust puff on impact
        if (!isLow) {
          const alpha = Math.sin(p * Math.PI) * 0.55;
          if (alpha > 0.02) {
            const alphaIdx = Math.round(alpha * 16);
            ctx.fillStyle = isWest ? ALPHA_LUT_DUST_SLATE[alphaIdx] : ALPHA_LUT_DUST_ORANGE[alphaIdx];
            const spread = p * 12;
            const bottomY = fy + MODULE_FRONT_H;
            ctx.beginPath();
            ctx.arc(fx - spread + 4, bottomY - 2, 3 + p * 3, 0, Math.PI * 2);
            ctx.arc(fx + W + spread - 4, bottomY - 2, 3 + p * 3, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    } else {
      const retractDist = Math.pow(progress, 1.9) * 450;
      hookY = targetRoofCenterY - 46 - retractDist;
      isAttached = false;
      slingDangle = progress;
    }

    if (hookY < -120) return;

    // 1. HAUPTZUGSEIL (Heavy Braided Steel Cable with Crimp Rings)
    ctx.strokeStyle = '#070a10';
    ctx.lineWidth = 4.2;
    ctx.beginPath();
    ctx.moveTo(hookX, -50);
    ctx.lineTo(hookX, hookY - 8);
    ctx.stroke();

    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(hookX, -50);
    ctx.lineTo(hookX, hookY - 8);
    ctx.stroke();

    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(hookX + 0.6, -50);
    ctx.lineTo(hookX + 0.6, hookY - 8);
    ctx.stroke();

    if (!isLow) {
      for (let my = hookY - 20; my > -40; my -= 24) {
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(hookX - 2.5, my, 5, 2.5);
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(hookX - 1.5, my + 0.5, 3, 1.5);
      }
    }

    // 2. HEBEGESCHIRR / SPREADER-TRAVERSE (Yellow/Black Hazard Plate)
    const spW = 34;
    const spH = 10;
    const spX = hookX - spW / 2;
    const spY = hookY - spH / 2;

    ctx.fillStyle = '#050810';
    ctx.fillRect(spX - 1.5, spY - 1.5, spW + 3, spH + 3);

    ctx.fillStyle = '#eab308';
    ctx.fillRect(spX, spY, spW, spH);

    if (!isLow) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(spX, spY, spW, spH);
      ctx.clip();
      ctx.fillStyle = '#0a0f18';
      for (let sx = spX - 10; sx < spX + spW + 10; sx += 8) {
        ctx.beginPath();
        ctx.moveTo(sx, spY + spH);
        ctx.lineTo(sx + 5, spY);
        ctx.lineTo(sx + 9, spY);
        ctx.lineTo(sx + 4, spY + spH);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }

    // Oberer Schäkel / Drehöse
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.arc(hookX, hookY - 7, 4.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    ctx.arc(hookX, hookY - 7, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0a0f18';
    ctx.beginPath();
    ctx.arc(hookX, hookY - 7, 1.2, 0, Math.PI * 2);
    ctx.fill();

    // Status-Leuchte (Grün = Halten, Orange = Ausklinken/Gelöst)
    ctx.fillStyle = isAttached ? '#22c55e' : '#f59e0b';
    ctx.beginPath();
    ctx.arc(hookX, hookY, 2.2, 0, Math.PI * 2);
    ctx.fill();

    // 3. VIERSTRANG-ANSCHLAGSEILE (Zu den 4 Dachecken des Moduls)
    const shadowBackX = isWest ? (fx + dir * shadowSkewX) + T / 2 : (fx + W + dir * shadowSkewX) - T / 2;
    const arenaBackX = isWest ? (fx + dir * skewX) + W - T / 2 : (fx + dir * skewX) + T / 2;

    for (let c = 0; c < 4; c++) {
      let eyeX, eyeY, cornerX, cornerY;
      if (c === 0) {
        eyeX = spX + 4; eyeY = spY + spH;
        cornerX = fx + 6; cornerY = fy + 3;
      } else if (c === 1) {
        eyeX = spX + spW - 4; eyeY = spY + spH;
        cornerX = fx + W - 6; cornerY = fy + 3;
      } else if (c === 2) {
        eyeX = spX + 9; eyeY = spY + spH - 2;
        cornerX = shadowBackX + (isWest ? 2 : -2); cornerY = by + 3;
      } else {
        eyeX = spX + spW - 9; eyeY = spY + spH - 2;
        cornerX = arenaBackX + (isWest ? -2 : 2); cornerY = by + 3;
      }

      if (isAttached) {
        ctx.strokeStyle = '#070a10';
        ctx.lineWidth = 2.6;
        ctx.beginPath();
        ctx.moveTo(eyeX, eyeY);
        if (sag > 0.5) {
          const midX = (eyeX + cornerX) / 2;
          const midY = (eyeY + cornerY) / 2 + sag;
          ctx.quadraticCurveTo(midX, midY, cornerX, cornerY);
        } else {
          ctx.lineTo(cornerX, cornerY);
        }
        ctx.stroke();

        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(eyeX, eyeY);
        if (sag > 0.5) {
          const midX = (eyeX + cornerX) / 2;
          const midY = (eyeY + cornerY) / 2 + sag;
          ctx.quadraticCurveTo(midX, midY, cornerX, cornerY);
        } else {
          ctx.lineTo(cornerX, cornerY);
        }
        ctx.stroke();

        // Corner Shackle / Pad Eye
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(cornerX - 2, cornerY - 3, 4, 4);
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(cornerX - 1.5, cornerY - 2.5, 3, 3);
      } else {
        const dangleLen = 38;
        const sway = Math.sin(slingDangle * 12 + c * 1.5) * 3.5;
        const endX = eyeX + (eyeX - hookX) * 0.3 + sway;
        const endY = eyeY + dangleLen;

        ctx.strokeStyle = '#070a10';
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        ctx.moveTo(eyeX, eyeY);
        ctx.quadraticCurveTo(eyeX + sway * 0.5, eyeY + dangleLen * 0.6, endX, endY);
        ctx.stroke();

        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(eyeX, eyeY);
        ctx.quadraticCurveTo(eyeX + sway * 0.5, eyeY + dangleLen * 0.6, endX, endY);
        ctx.stroke();

        // Hanging Hook Clip
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(endX - 2, endY - 1, 4, 5);
        ctx.fillStyle = '#e2e8f0';
        ctx.fillRect(endX - 1.5, endY, 3, 3);
      }
    }
  },

  // Congress Module Proclamation Speech Bubble (Variante A - Comic Scream / Shout Balloon)
  _drawCongressSpeechBubble(ctx, bubble, slotX, congY, dir, isLow) {
    if (!bubble || bubble.timer <= 0) return;

    const isWest = dir === 1;
    const fx = slotX - MODULE_W / 2;
    const fy = congY;
    const fh = MODULE_H;
    const cx = fx + (isWest ? MODULE_W * 0.48 : MODULE_W * 0.52) + dir * (MODULE_SKEW * 0.35);
    const cy = fy + fh * 0.45;

    // Timeline calculation: total duration is bubble.total (3.0s)
    const elapsed = Math.max(0, (bubble.total || 3.0) - bubble.timer);

    // 1. Enter scale (0.0s to 0.20s): bouncy energetic comic pop-in
    let enterScale = 1.0;
    if (elapsed < 0.20) {
      const p = elapsed / 0.20;
      const c1 = 1.85;
      const c3 = c1 + 1;
      enterScale = Math.max(0, 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2));
    }

    // 2. Exit fade and float (2.4s to 3.0s)
    let alpha = 1.0;
    let floatY = 0;
    if (elapsed > 2.4) {
      const exitP = Math.min(1, (elapsed - 2.4) / 0.6);
      alpha = Math.max(0, 1 - exitP);
      floatY = -exitP * 14;
    }

    const introFull = bubble.intro || 'A GREAT DECREE:';
    const decreeFull = bubble.decree || 'DECREE!';
    const decreeColor = bubble.color || '#00ff88';

    // Dynamic large typography calculation with caching on bubble object
    if (bubble._bubbleW === undefined || bubble._cachedDecree !== decreeFull || bubble._cachedIntro !== introFull) {
      const decreeFontSize = decreeFull.length > 18 ? 15 : (decreeFull.length > 14 ? 17 : 20);
      ctx.save();
      ctx.font = '900 12px "Orbitron", sans-serif';
      const introW = ctx.measureText(introFull).width;
      ctx.font = `900 ${decreeFontSize}px "Orbitron", sans-serif`;
      const decreeW = ctx.measureText(decreeFull).width;
      ctx.restore();
      const contentW = Math.max(introW, decreeW);
      bubble._cachedIntro = introFull;
      bubble._cachedDecree = decreeFull;
      bubble._introW = introW;
      bubble._decreeW = decreeW;
      bubble._decreeFontSize = decreeFontSize;
      bubble._bubbleW = Math.max(185, Math.min(245, contentW + 48));
    }

    const decreeFontSize = bubble._decreeFontSize;
    const bubbleW = bubble._bubbleW;
    const bubbleH = 58;

    // Position: higher (cy - 40) and shifted into the room (bx overlaps right 28px of module, extends into open sky)
    const bubbleX = cx + dir * (bubbleW * 0.5 + 16);
    const bubbleY = cy - 40;

    ctx.globalAlpha = alpha;
    ctx.translate(bubbleX, bubbleY + floatY);
    ctx.scale(enterScale, enterScale);
    ctx.translate(-bubbleX, -bubbleY);

    const bx = bubbleX - bubbleW / 2;
    const by = bubbleY - bubbleH / 2;

    const tipX = cx + dir * 8;
    const tipY = cy - 2;

    // 1. Pop-Art Comic Drop Shadow (crisp offset black shadow)
    ctx.save();
    ctx.translate(4, 4.5);
    _traceCongressScreamPath(ctx, bx, by, bubbleW, bubbleH, tipX, tipY, isWest);
    ctx.fillStyle = '#050811';
    ctx.fill();
    ctx.restore();

    // 2. Main Scream Balloon Body
    _traceCongressScreamPath(ctx, bx, by, bubbleW, bubbleH, tipX, tipY, isWest);
    let bgGrad = bubble._bgGrad;
    if (!bgGrad || bubble._bgGradBy !== by || bubble._bgGradH !== bubbleH) {
      bgGrad = ctx.createLinearGradient(bx, by, bx, by + bubbleH);
      bgGrad.addColorStop(0, '#ffffff');
      bgGrad.addColorStop(1, '#fffde8'); // warm comic paper tint
      bubble._bgGrad = bgGrad;
      bubble._bgGradBy = by;
      bubble._bgGradH = bubbleH;
    }
    ctx.fillStyle = bgGrad;
    ctx.fill();

    // 3. Heavy Comic Outline
    ctx.lineWidth = 3.2;
    ctx.strokeStyle = '#050811';
    ctx.lineJoin = 'miter';
    ctx.miterLimit = 4;
    ctx.stroke();

    // 4. Inner Scream Accent Rim in Decree Color
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(bx + 14, by + 4);
    ctx.lineTo(bx + bubbleW - 14, by + 4);
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = decreeColor;
    ctx.stroke();
    ctx.restore();

    // Phase 1: Rapid Typewriter Intro (0.04s - 0.52s)
    if (elapsed >= 0.04) {
      const typeP = Math.min(1, (elapsed - 0.04) / 0.48);
      const charCount = Math.min(introFull.length, Math.max(1, Math.ceil(typeP * introFull.length)));
      const currentIntro = introFull.slice(0, charCount);

      ctx.font = '900 12px "Orbitron", sans-serif';
      ctx.fillStyle = '#0f172a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(currentIntro, bubbleX, by + 18);
    }

    // Phase 2: Bold Comic Scream Punchline Stamp (at 0.75s)
    if (elapsed >= 0.75) {
      const punchP = Math.min(1, (elapsed - 0.75) / 0.14);
      // Explosive impact punch scale
      const punchScale = 1.45 - 0.45 * punchP;

      // Comic shout vibration jitter
      let jitterX = 0;
      let jitterY = 0;
      if (elapsed < 1.05) {
        const shake = Math.sin((elapsed - 0.75) * 55);
        jitterX = shake * 1.5;
        jitterY = Math.cos((elapsed - 0.75) * 55) * 1.0;
      }

      const stampX = bubbleX + jitterX;
      const stampY = by + 39 + jitterY;

      ctx.save();
      ctx.translate(stampX, stampY);
      ctx.scale(punchScale, punchScale);
      ctx.translate(-stampX, -stampY);

      // Shouting Comic Typography: Heavy Black Stroke + Neon Decree Color Fill
      ctx.font = `900 ${decreeFontSize}px "Orbitron", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 1. Thick solid black comic text outline
      ctx.lineWidth = 4.5;
      ctx.strokeStyle = '#050811';
      ctx.strokeText(decreeFull, stampX, stampY);

      // 2. Vibrant decree color fill
      ctx.fillStyle = decreeColor;
      ctx.fillText(decreeFull, stampX, stampY);

      // 3. Inner glossy core stroke for intense comic pop
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.strokeText(decreeFull, stampX, stampY);

      ctx.restore();
    }

    ctx.restore();
  },

  // Backward-compatible alias
  _drawDescentCable(ctx, x, y, progress, isLow) {
    this._drawCraneCableRig(ctx, x, y, y, 1, progress, false, 'western', isLow);
  }
};
